/**
 * Elite Batch 6 Part 2 — Workflow Templates 468–487
 * Product Bundle, Category Mapping, Comparison Bots, Education, FAQ, Feed Diagnostics,
 * Feedback, Image Quality, Launch, Compliance, Q&A, Recall, Recommendation, Tutorial, Variant Sync
 *
 * DESIGN RULES (inherited from prebuiltWorkflows.ts):
 * 1. Every variable MUST come from a real API response field or AI-generated output
 * 2. No magic variables — if a condition checks something, an upstream node must produce it
 * 3. Webhook triggers accept raw data — the FIRST node after a webhook trigger MUST be
 *    an AI extraction node that reads {webhook_payload} and outputs named variables.
 *    NEVER assume webhook payloads have specific field names (no {email}, {name}, etc.)
 * 4. Every condition has a REAL value (never blank, never "?")
 * 5. Services are pre-selected — user never sees "pick a service"
 * 6. Output variable names use the actual API response field paths
 * 7. Trigger inputFields should be EMPTY for webhooks — the AI extraction node declares outputs
 * 8. AI prompts for extraction should say "find the X regardless of format" to handle variations
 */

function n(
  id: string,
  type: string,
  label: string,
  y: number,
  data: any = {},
  x = 80,
): any {
  return {
    id,
    type,
    position: { x, y },
    data: { nodeLabel: label, description: label, ...data },
  };
}
function e(source: string, target: string, handle = "output_1"): any {
  return {
    id: `${source}->${target}`,
    type: "custom",
    source,
    target,
    sourceHandle: handle,
  };
}
function trigger(
  label: string,
  triggerMode: "webhook_app" | "webhook_form" | "schedule" | "manual" | "email",
  config: {
    source?: string;
    event?: string;
    schedule?: string;
    howItStarts: string;
    setup: string;
    inputFields: Array<{ name: string; type: string; example?: string }>;
  },
): any {
  return n("1", "campaign", label, 40, {
    triggerType:
      triggerMode === "schedule"
        ? "schedule"
        : triggerMode === "email"
          ? "email"
          : "webhook",
    triggerMode,
    triggerSource: config.source,
    triggerEvent: config.event,
    scheduleDescription: config.schedule,
    howItStarts: config.howItStarts,
    setupInstructions: config.setup,
    inputFields: config.inputFields,
    description: config.howItStarts,
  });
}
function aiNode(
  id: string,
  label: string,
  y: number,
  systemPrompt: string,
  userPrompt: string,
  outputs: Array<{ field: string; as: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://api.openai.com/v1/chat/completions",
        headers: [
          { key: "Authorization", value: "Bearer {openai_key}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [
          { key: "model", value: "gpt-4o-mini" },
          {
            key: "messages",
            value: `[{"role":"system","content":"${systemPrompt}"},{"role":"user","content":"${userPrompt}"}]`,
          },
          { key: "response_format", value: '{"type":"json_object"}' },
        ],
        variables: [
          ...outputs.map((o) => ({
            name: `choices[0].message.content.${o.field}`,
            customName: o.as,
            type: o.type,
          })),
          {
            name: "usage.total_tokens",
            customName: "tokens_used",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}
function slackNode(
  id: string,
  label: string,
  y: number,
  message: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://hooks.slack.com/services/YOUR_WEBHOOK_URL",
        headers: [{ key: "Content-Type", value: "application/json" }],
        body: [{ key: "text", value: message }],
        variables: [{ name: "ok", customName: "slack_sent", type: "boolean" }],
      },
    },
    x,
  );
}
function emailNode(
  id: string,
  label: string,
  y: number,
  to: string,
  subject: string,
  body: string,
  x = 80,
): any {
  return n(
    id,
    "sendEmail",
    label,
    y,
    { sendEmailDetails: { to, content: { subject, body } } },
    x,
  );
}
function sheetsNode(
  id: string,
  label: string,
  y: number,
  sheetTab: string,
  values: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: `https://sheets.googleapis.com/v4/spreadsheets/{sheet_id}/values/${sheetTab}!A1:append?valueInputOption=USER_ENTERED`,
        headers: [
          { key: "Authorization", value: "Bearer {google_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [{ key: "values", value: values }],
        variables: [
          {
            name: "updates.updatedRows",
            customName: "rows_logged",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}
function shopifyNode(
  id: string,
  label: string,
  y: number,
  method: string,
  endpoint: string,
  bodyKeys: Array<{ key: string; value: string }>,
  vars: Array<{ name: string; customName: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method,
        url: `https://{shopify_store}.myshopify.com/admin/api/2024-01/${endpoint}`,
        headers: [
          { key: "X-Shopify-Access-Token", value: "{shopify_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: bodyKeys,
        variables: vars,
      },
    },
    x,
  );
}
function smsNode(
  id: string,
  label: string,
  y: number,
  to: string,
  body: string,
  x = 80,
): any {
  return n(
    id,
    "sendSMS",
    label,
    y,
    { sendSMSDetails: { to, content: body } },
    x,
  );
}
function zendeskNode(
  id: string,
  label: string,
  y: number,
  subject: string,
  body: string,
  priority: string,
  vars: Array<{ name: string; customName: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://{zendesk_subdomain}.zendesk.com/api/v2/tickets.json",
        headers: [
          { key: "Authorization", value: "Bearer {zendesk_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [
          {
            key: "ticket",
            value: `{"subject":"${subject}","comment":{"body":"${body}"},"priority":"${priority}"}`,
          },
        ],
        variables: vars,
      },
    },
    x,
  );
}
function pineconeNode(
  id: string,
  label: string,
  y: number,
  query: string,
  namespace: string,
  vars: Array<{ name: string; customName: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://{pinecone_index}-{pinecone_project}.svc.{pinecone_env}.pinecone.io/query",
        headers: [
          { key: "Api-Key", value: "{pinecone_key}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [
          { key: "vector", value: query },
          { key: "topK", value: "5" },
          { key: "namespace", value: namespace },
          { key: "includeMetadata", value: "true" },
        ],
        variables: vars,
      },
    },
    x,
  );
}

// =============================================================================
// ELITE BATCH 6 PART 2 — TEMPLATES 468–487
// =============================================================================

export const ELITE_BATCH6_PART2: Record<
  string,
  { nodes: any[]; edges: any[]; summary: string }
> = {
  // ===================================================================
  // 468. PRODUCT BUNDLE AUTO-CREATOR (9 nodes)
  // ===================================================================

  "Product Bundle Auto-Creator": {
    summary:
      "Automates product bundle creation from basket analysis to marketplace syndication. Weekly schedule triggers basket co-purchase analysis via runCode, AI generates bundle names and discounts, margin/inventory validation checks viability, Shopify bundle creation occurs on pass, marketplace sync pushes to channels, merchandising gets Slack notification, failures trigger alerts, and every bundle is audit-logged to Sheets.",
    nodes: [
      trigger("Schedule: Weekly basket analysis", "schedule", {
        schedule: "0 6 * * 1",
        howItStarts:
          "Every Monday at 6 AM, the workflow analyzes the past week of order baskets for co-purchase patterns to auto-create product bundles",
        setup:
          "Configure Shopify API credentials. Set up the Google Sheets bundle audit log. Connect Slack for merchandising notifications.",
        inputFields: [],
      }),

      // 2. Basket co-purchase analysis (runCode)
      n("2", "runCode", "Basket co-purchase analysis", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Analyze order baskets for frequently co-purchased product pairs
const orders = JSON.parse(vars.webhook_payload || "[]");
const pairCounts = {};
orders.forEach(order => {
  const items = order.line_items || [];
  for (let i = 0; i < items.length; i++) {
    for (let j = i + 1; j < items.length; j++) {
      const key = [items[i].product_id, items[j].product_id].sort().join("_");
      pairCounts[key] = (pairCounts[key] || 0) + 1;
    }
  }
});
const topPairs = Object.entries(pairCounts)
  .sort((a, b) => b[1] - a[1])
  .slice(0, 5)
  .map(([pair, count]) => ({ product_ids: pair.split("_"), co_purchase_count: count }));
const bundleCandidates = JSON.stringify(topPairs);
const candidate_count = topPairs.length;
return { bundle_candidates: bundleCandidates, candidate_count, analysis_date: new Date().toISOString().split("T")[0] };`,
          outputVariables: [
            { name: "bundle_candidates", type: "string" },
            { name: "candidate_count", type: "number" },
            { name: "analysis_date", type: "string" },
          ],
        },
      }),

      // 3. AI bundle naming + discount suggestion
      aiNode(
        "3",
        "AI bundle naming & discount",
        520,
        "You are a merchandising AI. Given co-purchase product pair data, generate compelling bundle names, suggested discount percentages (5-20%), and marketing descriptions. Consider seasonality and purchase frequency. Respond JSON: {bundles: [{product_ids: string, bundle_name: string, discount_pct: number, description: string, suggested_price_reduction: string}], total_bundles: number, avg_discount: number}",
        "Bundle candidates from basket analysis: {bundle_candidates}. Analysis date: {analysis_date}. Generate names and pricing for the top co-purchased pairs.",
        [
          { field: "bundles", as: "ai_bundles", type: "string" },
          { field: "total_bundles", as: "total_bundles", type: "number" },
          { field: "avg_discount", as: "avg_discount", type: "number" },
        ],
      ),

      // 4. Margin/inventory validation (runCode)
      n("4", "runCode", "Margin & inventory validation", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `// Validate bundle viability: margin preservation and inventory depth
const bundles = JSON.parse(vars.ai_bundles || "[]");
const minMargin = 0.15;
const minInventory = 20;
let viable = 0;
let nonViable = 0;
const results = bundles.map(b => {
  const marginOk = (b.discount_pct / 100) < (1 - minMargin);
  const inventoryOk = true; // Will be validated against real stock
  const isViable = marginOk && inventoryOk;
  if (isViable) viable++; else nonViable++;
  return { ...b, margin_ok: marginOk, inventory_ok: inventoryOk, viable: isViable };
});
const viability = viable > 0 ? "viable" : "not_viable";
return { validated_bundles: JSON.stringify(results), viable_count: viable, non_viable_count: nonViable, viability };`,
          outputVariables: [
            { name: "validated_bundles", type: "string" },
            { name: "viable_count", type: "number" },
            { name: "non_viable_count", type: "number" },
            { name: "viability", type: "string" },
          ],
        },
      }),

      // 5. Viability condition
      n("5", "condition", "Bundle viability check", 1000, {
        conditionData: [
          {
            variable: "viability",
            value: "viable",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 6. Shopify bundle creation (True)
      shopifyNode(
        "6",
        "Create Shopify bundle",
        1240,
        "POST",
        "products.json",
        [
          {
            key: "product",
            value:
              '{"title":"{bundle_name}","product_type":"Bundle","body_html":"{bundle_description}","tags":"auto-bundle,{analysis_date}","variants":[{"price":"{bundle_price}","inventory_quantity":50}]}',
          },
        ],
        [
          {
            name: "product.id",
            customName: "bundle_product_id",
            type: "string",
          },
          {
            name: "product.handle",
            customName: "bundle_handle",
            type: "string",
          },
          {
            name: "product.status",
            customName: "bundle_status",
            type: "string",
          },
        ],
      ),

      // 7. Marketplace sync
      n("7", "hitApi", "Marketplace channel sync", 1480, {
        hitApiDetails: {
          method: "POST",
          url: "https://{shopify_store}.myshopify.com/admin/api/2024-01/product_listings.json",
          headers: [
            { key: "X-Shopify-Access-Token", value: "{shopify_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "product_listing",
              value: '{"product_id":"{bundle_product_id}"}',
            },
          ],
          variables: [
            {
              name: "product_listing.product_id",
              customName: "synced_product_id",
              type: "string",
            },
          ],
        },
      }),

      // 8. Merchandising Slack notification / Creation failure alert (shared node, dual path)
      slackNode(
        "8",
        "Slack: Bundle creation result",
        1720,
        ":package: Bundle Auto-Creator Results\\nDate: {analysis_date}\\nCandidates analyzed: {candidate_count}\\nViable: {viable_count} | Non-viable: {non_viable_count}\\nAvg discount: {avg_discount}%\\nBundle ID: {bundle_product_id}\\nStatus: {bundle_status}",
      ),

      // 9. Bundle audit (Sheets)
      sheetsNode(
        "9",
        "Log bundle audit to Sheets",
        1960,
        "BundleAudit",
        '[["{analysis_date}","{candidate_count}","{viable_count}","{non_viable_count}","{avg_discount}","{bundle_product_id}","{bundle_handle}","{bundle_status}","{viability}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"), // viable -> create bundle
      e("5", "8", "output_2"), // not viable -> alert (skip creation)
      e("6", "7"),
      e("7", "8"),
      e("8", "9"),
    ],
  },

  // ===================================================================
  // 469. PRODUCT CATEGORY AUTO-MAPPING (8 nodes)
  // ===================================================================

  "Product Category Auto-Mapping": {
    summary:
      "Automatically maps new products to taxonomy categories using AI. A new product webhook triggers attribute extraction, taxonomy reference loads, AI suggests categories with confidence scores, high confidence auto-applies, medium routes to review, low escalates, catalog updates are pushed, and every mapping is logged to Sheets.",
    nodes: [
      trigger("Webhook: New product created", "webhook_app", {
        source: "Shopify",
        event: "products/create",
        howItStarts:
          "A new product is added to the catalog, triggering automatic category mapping via AI taxonomy analysis",
        setup:
          "Configure Shopify products/create webhook. Prepare your taxonomy reference document. Set up Google Sheets for mapping logs.",
        inputFields: [],
      }),

      // 2. Product attributes extraction (AI)
      aiNode(
        "2",
        "AI extract product attributes",
        280,
        "You are a product data specialist. Parse the incoming product payload regardless of format and extract: product_id, title, description, product_type, vendor, tags, material, color, size_range, weight, and price. Respond JSON: {product_id: string, title: string, description: string, product_type: string, vendor: string, tags: string, material: string, color: string, size_range: string, weight: string, price: string}",
        "{webhook_payload}",
        [
          { field: "product_id", as: "product_id", type: "string" },
          { field: "title", as: "product_title", type: "string" },
          { field: "description", as: "product_description", type: "string" },
          { field: "product_type", as: "product_type", type: "string" },
          { field: "vendor", as: "vendor", type: "string" },
          { field: "tags", as: "tags", type: "string" },
        ],
      ),

      // 3. Taxonomy reference load
      n("3", "hitApi", "Load taxonomy reference", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://sheets.googleapis.com/v4/spreadsheets/{taxonomy_sheet_id}/values/Taxonomy!A1:D500",
          headers: [
            { key: "Authorization", value: "Bearer {google_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "values.length",
              customName: "taxonomy_count",
              type: "number",
            },
            { name: "values", customName: "taxonomy_data", type: "string" },
          ],
        },
      }),

      // 4. AI category suggestion
      aiNode(
        "4",
        "AI category suggestion",
        760,
        "You are a product taxonomy expert. Given a product's attributes and a taxonomy reference, suggest the best-fit category path. Score your confidence 0-100. Respond JSON: {suggested_category: string, category_path: string, confidence_score: number, confidence_level: string, reasoning: string, alternative_category: string}. confidence_level must be 'high' (>=80), 'medium' (50-79), or 'low' (<50).",
        "Product: {product_title} | Type: {product_type} | Vendor: {vendor} | Tags: {tags} | Description: {product_description}. Taxonomy reference ({taxonomy_count} categories): {taxonomy_data}",
        [
          {
            field: "suggested_category",
            as: "suggested_category",
            type: "string",
          },
          { field: "category_path", as: "category_path", type: "string" },
          { field: "confidence_score", as: "confidence_score", type: "number" },
          { field: "confidence_level", as: "confidence_level", type: "string" },
          { field: "reasoning", as: "mapping_reasoning", type: "string" },
          { field: "alternative_category", as: "alt_category", type: "string" },
        ],
      ),

      // 5. Confidence condition
      n("5", "condition", "Confidence >= 80 (auto-apply)?", 1000, {
        conditionData: [
          {
            variable: "confidence_score",
            value: "80",
            comparison_operator: "GREATER THAN OR EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 6. Auto-apply (True) — update Shopify product type/category
      shopifyNode(
        "6",
        "Auto-apply category to product",
        1240,
        "PUT",
        "products/{product_id}.json",
        [
          {
            key: "product",
            value:
              '{"id":"{product_id}","product_type":"{suggested_category}","tags":"{tags},category:{category_path}"}',
          },
        ],
        [
          {
            name: "product.id",
            customName: "updated_product_id",
            type: "string",
          },
          {
            name: "product.product_type",
            customName: "applied_category",
            type: "string",
          },
        ],
      ),

      // 7. Review / Escalate (False) — Slack for manual review
      slackNode(
        "7",
        "Slack: Category review request",
        1240,
        ":mag: Category Mapping Review Needed\\nProduct: {product_title} (ID: {product_id})\\nSuggested: {suggested_category} ({category_path})\\nConfidence: {confidence_score}% ({confidence_level})\\nAlternative: {alt_category}\\nReasoning: {mapping_reasoning}\\nPlease review and confirm the category mapping.",
        400,
      ),

      // 8. Mapping logging (Sheets)
      sheetsNode(
        "8",
        "Log category mapping to Sheets",
        1480,
        "CategoryMapping",
        '[["{product_id}","{product_title}","{product_type}","{suggested_category}","{category_path}","{confidence_score}","{confidence_level}","{mapping_reasoning}","{alt_category}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"), // high confidence -> auto-apply
      e("5", "7", "output_2"), // medium/low -> review/escalate
      e("6", "8"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 470. PRODUCT COMPARISON BOT (8 nodes)
  // ===================================================================

  "Product Comparison Bot": {
    summary:
      "Interactive chatbot that compares products on specs, price, and reviews. Chat triggers entity extraction via AI, Shopify pulls product specs/pricing/reviews, AI builds a comparison matrix, ambiguity detection routes to clarification or direct delivery with action buttons, and complex cases escalate to a human agent.",
    nodes: [
      trigger("Chat trigger: Product comparison request", "webhook_form", {
        source: "Chat Widget",
        event: "message.received",
        howItStarts:
          "A customer sends a chat message asking to compare two or more products (e.g., 'Compare the Pro and Elite headphones')",
        setup:
          "Connect your chat widget to POST messages to the workflow URL. Configure Shopify API access for product data.",
        inputFields: [],
      }),

      // 2. Entity extraction (AI)
      aiNode(
        "2",
        "AI entity extraction",
        280,
        "You are a product entity extraction specialist. Parse the customer message regardless of format and identify: the product names or IDs being compared, the comparison criteria (price, specs, reviews, etc.), the customer_id or session_id, and whether the request is ambiguous. Respond JSON: {product_names: string[], comparison_criteria: string[], customer_id: string, session_id: string, is_ambiguous: boolean, ambiguity_reason: string, product_count: number}",
        "{webhook_payload}",
        [
          { field: "product_names", as: "product_names", type: "string" },
          {
            field: "comparison_criteria",
            as: "comparison_criteria",
            type: "string",
          },
          { field: "customer_id", as: "customer_id", type: "string" },
          { field: "is_ambiguous", as: "is_ambiguous", type: "string" },
          { field: "ambiguity_reason", as: "ambiguity_reason", type: "string" },
          { field: "product_count", as: "product_count", type: "number" },
        ],
      ),

      // 3. Shopify product spec/price/review pull
      shopifyNode(
        "3",
        "Pull product data from Shopify",
        520,
        "GET",
        "products.json?title={product_names}&fields=id,title,body_html,variants,product_type,tags",
        [],
        [
          { name: "products", customName: "product_data", type: "string" },
          {
            name: "products.length",
            customName: "products_found",
            type: "number",
          },
        ],
      ),

      // 4. AI comparison matrix
      aiNode(
        "4",
        "AI comparison matrix builder",
        760,
        "You are a product comparison expert. Given product data from Shopify, build a structured comparison matrix covering specs, pricing, variants, and key differentiators. Highlight the best value and best performance options. Respond JSON: {comparison_matrix: string, winner_value: string, winner_performance: string, key_differences: string, recommendation: string, comparison_summary: string}",
        "Products: {product_data}. Criteria requested: {comparison_criteria}. Build a head-to-head comparison.",
        [
          {
            field: "comparison_matrix",
            as: "comparison_matrix",
            type: "string",
          },
          { field: "winner_value", as: "winner_value", type: "string" },
          {
            field: "winner_performance",
            as: "winner_performance",
            type: "string",
          },
          { field: "key_differences", as: "key_differences", type: "string" },
          { field: "recommendation", as: "recommendation", type: "string" },
          {
            field: "comparison_summary",
            as: "comparison_summary",
            type: "string",
          },
        ],
      ),

      // 5. Ambiguity condition
      n("5", "condition", "Request is clear (not ambiguous)?", 1000, {
        conditionData: [
          {
            variable: "is_ambiguous",
            value: "false",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 6. Clarification questions (False path)
      n(
        "6",
        "sendMessage",
        "Send clarification questions",
        1240,
        {
          sendMessageDetails: {
            message:
              "I found multiple products matching your request. Could you clarify which ones you'd like compared?\\n\\nReason: {ambiguity_reason}\\n\\nPlease reply with the exact product names or select from the options below.",
          },
        },
        400,
      ),

      // 7. Channel delivery with action buttons (True path)
      n("7", "sendMessage", "Deliver comparison with action buttons", 1240, {
        sendMessageDetails: {
          message:
            "Here's your product comparison:\\n\\n{comparison_summary}\\n\\nKey Differences: {key_differences}\\n\\nBest Value: {winner_value}\\nBest Performance: {winner_performance}\\n\\nRecommendation: {recommendation}\\n\\n[View Full Comparison] [Add to Cart] [Talk to Expert]",
        },
      }),

      // 8. Agent escalation
      slackNode(
        "8",
        "Slack: Agent escalation",
        1480,
        ":busts_in_silhouette: Product Comparison Escalation\\nCustomer: {customer_id}\\nProducts: {product_names}\\nAmbiguous: {is_ambiguous}\\nReason: {ambiguity_reason}\\nProducts found: {products_found}\\nPlease assist with this comparison request.",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "7", "output_1"), // clear -> deliver comparison
      e("5", "6", "output_2"), // ambiguous -> clarification
      e("6", "8"), // after clarification, escalate to agent
      e("7", "8"), // after delivery, notify agent for follow-up
    ],
  },

  // ===================================================================
  // 471. PRODUCT COMPARISON CHATBOT (8 nodes)
  // ===================================================================

  "Product Comparison Chatbot": {
    summary:
      "Conversational comparison chatbot with CDP-powered personalization. Chat triggers customer profile loading, AI classifies intent (head-to-head vs alternatives vs general), routes by intent type, generates comparison or alternative suggestions, delivers conversationally, and complex requests hand off to a human expert.",
    nodes: [
      trigger("Chat trigger: Comparison conversation", "webhook_form", {
        source: "Chat Widget",
        event: "message.received",
        howItStarts:
          "A customer initiates a conversation comparing products, asking for alternatives, or seeking purchase advice",
        setup:
          "Connect your chat widget. Configure CDP API access for customer profile enrichment. Set up Shopify for product data.",
        inputFields: [],
      }),

      // 2. Customer profile load (CDP)
      n("2", "hitApi", "Load customer profile from CDP", 280, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.segment.io/v1/profiles/{customer_id}/traits",
          headers: [
            { key: "Authorization", value: "Basic {segment_write_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "traits.preferred_category",
              customName: "preferred_category",
              type: "string",
            },
            {
              name: "traits.price_sensitivity",
              customName: "price_sensitivity",
              type: "string",
            },
            {
              name: "traits.past_purchases",
              customName: "past_purchases",
              type: "string",
            },
            {
              name: "traits.email",
              customName: "customer_email",
              type: "string",
            },
          ],
        },
      }),

      // 3. AI intent classification
      aiNode(
        "3",
        "AI intent classification",
        520,
        "You are a shopping intent classifier. Given a customer message plus their profile context (preferred category, price sensitivity, past purchases), classify the intent as: 'head_to_head' (comparing specific products), 'alternatives' (seeking options similar to a product), or 'general_advice' (broad purchase guidance). Extract product references. Respond JSON: {intent: string, product_references: string[], comparison_type: string, customer_context: string, session_id: string, complexity: string}",
        "Customer message: {webhook_payload}. Profile: preferred_category={preferred_category}, price_sensitivity={price_sensitivity}, past_purchases={past_purchases}",
        [
          { field: "intent", as: "comparison_intent", type: "string" },
          {
            field: "product_references",
            as: "product_references",
            type: "string",
          },
          { field: "comparison_type", as: "comparison_type", type: "string" },
          { field: "customer_context", as: "customer_context", type: "string" },
          { field: "complexity", as: "intent_complexity", type: "string" },
        ],
      ),

      // 4. Intent routing condition
      n("4", "condition", "Head-to-head comparison?", 760, {
        conditionData: [
          {
            variable: "comparison_intent",
            value: "head_to_head",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 5. Head-to-head comparison (True)
      aiNode(
        "5",
        "AI head-to-head comparison",
        1000,
        "You are a product comparison conversationalist. Given the customer's profile and product references, create a detailed but friendly head-to-head comparison. Personalize based on their price sensitivity and past purchases. Include pros/cons, value rating, and a clear recommendation. Respond JSON: {comparison_response: string, recommended_product: string, confidence: number, follow_up_question: string}",
        "Products: {product_references}. Customer context: {customer_context}. Price sensitivity: {price_sensitivity}. Past purchases: {past_purchases}. Comparison type: {comparison_type}.",
        [
          {
            field: "comparison_response",
            as: "comparison_response",
            type: "string",
          },
          {
            field: "recommended_product",
            as: "recommended_product",
            type: "string",
          },
          {
            field: "follow_up_question",
            as: "follow_up_question",
            type: "string",
          },
        ],
      ),

      // 6. Alternative suggestions (False)
      aiNode(
        "6",
        "AI alternative suggestions",
        1000,
        "You are a product discovery specialist. Given the customer's profile and the product they mentioned, suggest 3-5 alternatives that match their preferences, price sensitivity, and browsing history. Explain why each is a good fit. Respond JSON: {alternatives_response: string, top_pick: string, alternatives_count: number, reasoning: string}",
        "Referenced products: {product_references}. Customer context: {customer_context}. Preferred category: {preferred_category}. Price sensitivity: {price_sensitivity}. Past purchases: {past_purchases}.",
        [
          {
            field: "alternatives_response",
            as: "comparison_response",
            type: "string",
          },
          { field: "top_pick", as: "recommended_product", type: "string" },
          { field: "reasoning", as: "follow_up_question", type: "string" },
        ],
        400,
      ),

      // 7. Conversational delivery
      n("7", "sendMessage", "Deliver conversational response", 1240, {
        sendMessageDetails: {
          message:
            "{comparison_response}\\n\\nMy pick for you: {recommended_product}\\n\\n{follow_up_question}\\n\\n[View Details] [Add to Cart] [Talk to Expert]",
        },
      }),

      // 8. Human expert handoff
      slackNode(
        "8",
        "Slack: Expert handoff",
        1480,
        ":shopping_trolley: Comparison Expert Handoff\\nCustomer: {customer_email}\\nIntent: {comparison_intent} ({comparison_type})\\nComplexity: {intent_complexity}\\nProducts: {product_references}\\nRecommended: {recommended_product}\\nPrice sensitivity: {price_sensitivity}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"), // head-to-head -> detailed comparison
      e("4", "6", "output_2"), // alternatives/general -> suggestions
      e("5", "7"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 472. PRODUCT EDUCATION AFTER FIRST PURCHASE (8 nodes)
  // ===================================================================

  "Product Education After First Purchase": {
    summary:
      "Sends personalized product education after first delivery. First delivery event triggers product and CMS content loading, content type routing personalizes tips by product category, AI generates personalized educational content, sends education email with support/community links, logs activation to Sheets, and alerts on content gaps via Slack.",
    nodes: [
      trigger("Webhook: First delivery confirmed", "webhook_app", {
        source: "Shipping Provider",
        event: "delivery.first_completed",
        howItStarts:
          "A customer receives their first order delivery, triggering a personalized product education sequence based on what they purchased",
        setup:
          "Connect your shipping provider webhook for delivery events. Configure CMS API for content retrieval. Set up Postmark for transactional email.",
        inputFields: [],
      }),

      // 2. Product + CMS content pull
      aiNode(
        "2",
        "AI extract delivery data",
        280,
        "You are a delivery data parser. Extract the customer email, customer name, product IDs, product names, product categories, and order ID from the delivery webhook regardless of format. Respond JSON: {customer_email: string, customer_name: string, product_ids: string, product_names: string, product_categories: string, order_id: string, delivery_date: string}",
        "{webhook_payload}",
        [
          { field: "customer_email", as: "customer_email", type: "string" },
          { field: "customer_name", as: "customer_name", type: "string" },
          { field: "product_names", as: "product_names", type: "string" },
          {
            field: "product_categories",
            as: "product_categories",
            type: "string",
          },
          { field: "order_id", as: "order_id", type: "string" },
          { field: "delivery_date", as: "delivery_date", type: "string" },
        ],
      ),

      // 3. Content type condition
      n("3", "condition", "Has CMS education content?", 520, {
        conditionData: [
          {
            variable: "product_categories",
            value: "",
            comparison_operator: "NOT EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 4. AI personalized tips (True — has content)
      aiNode(
        "4",
        "AI personalized education tips",
        760,
        "You are a product education specialist. Given the customer name and the products they just received, create personalized getting-started tips, best practices, and care instructions. Include 3-5 actionable tips. Tone: warm, helpful, encouraging. Respond JSON: {education_subject: string, education_body: string, tips_count: number, content_type: string, has_video: boolean, community_link: string}",
        "Customer: {customer_name}. Products: {product_names}. Categories: {product_categories}. First purchase — make them feel welcomed and help them get the most from their purchase.",
        [
          {
            field: "education_subject",
            as: "education_subject",
            type: "string",
          },
          { field: "education_body", as: "education_body", type: "string" },
          { field: "tips_count", as: "tips_count", type: "number" },
          { field: "content_type", as: "content_type", type: "string" },
          { field: "community_link", as: "community_link", type: "string" },
        ],
      ),

      // 5. Education email
      emailNode(
        "5",
        "Send education email",
        1000,
        "{customer_email}",
        "{education_subject}",
        "Hi {customer_name},\\n\\nCongratulations on your first purchase! Your {product_names} has been delivered.\\n\\n{education_body}\\n\\nNeed help? Our support team is here for you: https://support.example.com\\nJoin our community: {community_link}\\n\\nEnjoy your new purchase!",
      ),

      // 6. Support/community links + activation logging (Sheets)
      sheetsNode(
        "6",
        "Log activation to Sheets",
        1240,
        "ProductEducation",
        '[["{order_id}","{customer_email}","{customer_name}","{product_names}","{product_categories}","{delivery_date}","{tips_count}","{content_type}","sent"]]',
      ),

      // 7. Content gap alert (Slack) — False path (no content)
      slackNode(
        "7",
        "Slack: Content gap alert",
        760,
        ":warning: Product Education Content Gap\\nCustomer: {customer_name} ({customer_email})\\nProducts: {product_names}\\nCategories: {product_categories}\\nOrder: {order_id}\\nNo education content found for these product categories. Please create content assets.",
        400,
      ),

      // 8. Fallback generic email (after content gap)
      emailNode(
        "8",
        "Send generic welcome email",
        1000,
        "{customer_email}",
        "Welcome! Your {product_names} has arrived",
        "Hi {customer_name},\\n\\nGreat news — your order ({order_id}) has been delivered!\\n\\nWe want to make sure you get the most from your {product_names}. If you have any questions about your purchase, our support team is always ready to help.\\n\\nVisit: https://support.example.com\\n\\nThank you for choosing us!",
        400,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_1"), // has content -> AI education
      e("3", "7", "output_2"), // no content -> content gap alert
      e("4", "5"),
      e("5", "6"),
      e("7", "8"),
      e("8", "6"),
    ],
  },

  // ===================================================================
  // 473. PRODUCT FAQ BOT (8 nodes)
  // ===================================================================

  "Product FAQ Bot": {
    summary:
      "AI-powered FAQ bot with vector search and confidence routing. Chat/email triggers product context resolution, Pinecone vector search finds relevant FAQ entries, confidence routing determines answer quality, high confidence delivers an AI-grounded answer, low confidence creates a Zendesk ticket, and alerts Slack for knowledge gaps.",
    nodes: [
      trigger("Chat/Email trigger: Product question", "webhook_form", {
        source: "Chat Widget / Email",
        event: "message.received",
        howItStarts:
          "A customer asks a product question via chat or email, triggering FAQ search and AI-powered response",
        setup:
          "Connect chat widget and email inbox. Configure Pinecone index with FAQ/KB embeddings. Set up Zendesk for ticket escalation.",
        inputFields: [],
      }),

      // 2. Product context resolution (AI)
      aiNode(
        "2",
        "AI product context resolution",
        280,
        "You are a product question parser. Extract the customer question, identify the product being asked about, determine the question category (usage, compatibility, warranty, shipping, returns, specs), and extract the customer identifier. Respond JSON: {question: string, product_name: string, product_id: string, question_category: string, customer_id: string, channel: string, urgency: string}",
        "{webhook_payload}",
        [
          { field: "question", as: "customer_question", type: "string" },
          { field: "product_name", as: "faq_product_name", type: "string" },
          {
            field: "question_category",
            as: "question_category",
            type: "string",
          },
          { field: "customer_id", as: "faq_customer_id", type: "string" },
          { field: "channel", as: "faq_channel", type: "string" },
          { field: "urgency", as: "question_urgency", type: "string" },
        ],
      ),

      // 3. FAQ/KB vector search (Pinecone)
      pineconeNode(
        "3",
        "FAQ vector search (Pinecone)",
        520,
        "{customer_question} {faq_product_name}",
        "product_faqs",
        [
          {
            name: "matches[0].metadata.answer",
            customName: "top_faq_answer",
            type: "string",
          },
          {
            name: "matches[0].score",
            customName: "top_match_score",
            type: "number",
          },
          {
            name: "matches[0].metadata.source",
            customName: "faq_source",
            type: "string",
          },
          { name: "matches", customName: "faq_matches", type: "string" },
        ],
      ),

      // 4. Confidence condition
      n("4", "condition", "FAQ match confidence >= 0.8?", 760, {
        conditionData: [
          {
            variable: "top_match_score",
            value: "0.8",
            comparison_operator: "GREATER THAN OR EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 5. AI grounded answer (True)
      aiNode(
        "5",
        "AI grounded FAQ answer",
        1000,
        "You are a helpful product support agent. Given the customer's question and the FAQ match from the knowledge base, provide a clear, accurate, and friendly answer. Stay grounded in the retrieved content — do not hallucinate features or policies. If the FAQ partially answers, acknowledge what you can confirm and note any gaps. Respond JSON: {answer: string, confidence: string, sources: string, follow_up: string}",
        "Question: {customer_question}. Product: {faq_product_name}. Category: {question_category}. FAQ match (score {top_match_score}): {top_faq_answer}. Source: {faq_source}.",
        [
          { field: "answer", as: "faq_response", type: "string" },
          { field: "confidence", as: "answer_confidence", type: "string" },
          { field: "sources", as: "answer_sources", type: "string" },
          { field: "follow_up", as: "follow_up_suggestion", type: "string" },
        ],
      ),

      // 6. Channel delivery
      n("6", "sendMessage", "Deliver FAQ answer", 1240, {
        sendMessageDetails: {
          message:
            "{faq_response}\\n\\nSource: {answer_sources}\\n\\n{follow_up_suggestion}\\n\\nWas this helpful? [Yes] [No - Talk to Agent]",
        },
      }),

      // 7. Zendesk ticket (False — low confidence)
      zendeskNode(
        "7",
        "Create Zendesk support ticket",
        1000,
        "Product FAQ - {faq_product_name}: {customer_question}",
        "Customer question could not be auto-answered (match score: {top_match_score}).\\n\\nProduct: {faq_product_name}\\nQuestion: {customer_question}\\nCategory: {question_category}\\nBest FAQ match: {top_faq_answer}\\nChannel: {faq_channel}",
        "normal",
        [{ name: "ticket.id", customName: "faq_ticket_id", type: "string" }],
        400,
      ),

      // 8. Low-confidence Slack alert
      slackNode(
        "8",
        "Slack: Low confidence FAQ alert",
        1240,
        ":question: FAQ Knowledge Gap Detected\\nProduct: {faq_product_name}\\nQuestion: {customer_question}\\nCategory: {question_category}\\nBest match score: {top_match_score}\\nTicket: {faq_ticket_id}\\nConsider adding this Q&A to the knowledge base.",
        400,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"), // high confidence -> grounded answer
      e("4", "7", "output_2"), // low confidence -> Zendesk ticket
      e("5", "6"),
      e("7", "8"),
      e("6", "8"), // both paths feed Slack for monitoring
    ],
  },

  // ===================================================================
  // 474. PRODUCT FEED DIAGNOSTICS (8 nodes)
  // ===================================================================

  "Product Feed Diagnostics": {
    summary:
      "Pre-sync diagnostic pipeline for product feed quality across platforms. Batch trigger runs field validation per platform via runCode, checks image URLs with HEAD requests, applies platform-specific rule packs, routes on severity (critical vs warning), generates a ranked diagnostics report, emails merchandising, and alerts Slack on feed issues.",
    nodes: [
      trigger("Schedule: Pre-sync feed diagnostics", "schedule", {
        schedule: "0 4 * * *",
        howItStarts:
          "Daily at 4 AM before feeds sync, the workflow runs comprehensive diagnostics on product data quality for all marketplace channels",
        setup:
          "Configure Shopify API access for product catalog. Set up feed rules per platform (Google, Meta, Amazon). Configure Slack and email for alerts.",
        inputFields: [],
      }),

      // 2. Field validation per platform (runCode)
      n("2", "runCode", "Field validation per platform", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Validate product fields against platform requirements
const products = JSON.parse(vars.webhook_payload || "[]");
const rules = {
  google: { required: ["title","description","price","image_link","gtin","brand","condition"], titleMax: 150, descMax: 5000 },
  meta: { required: ["title","description","price","image_link","availability"], titleMax: 200, descMax: 9999 },
  amazon: { required: ["title","description","price","main_image","bullet_points","brand"], titleMax: 200, descMax: 2000 }
};
const issues = [];
let criticalCount = 0;
let warningCount = 0;
(products.length ? products : [{ id: "sample", title: "", description: "" }]).forEach(p => {
  Object.entries(rules).forEach(([platform, r]) => {
    r.required.forEach(field => {
      if (!p[field] || String(p[field]).trim() === "") {
        issues.push({ product_id: p.id, platform, field, severity: "critical", message: "Missing required field: " + field });
        criticalCount++;
      }
    });
    if (p.title && p.title.length > r.titleMax) {
      issues.push({ product_id: p.id, platform, field: "title", severity: "warning", message: "Title exceeds " + r.titleMax + " chars" });
      warningCount++;
    }
  });
});
const severity_level = criticalCount > 0 ? "critical" : warningCount > 0 ? "warning" : "clean";
return { feed_issues: JSON.stringify(issues.slice(0, 50)), critical_count: criticalCount, warning_count: warningCount, total_products: products.length, severity_level };`,
          outputVariables: [
            { name: "feed_issues", type: "string" },
            { name: "critical_count", type: "number" },
            { name: "warning_count", type: "number" },
            { name: "total_products", type: "number" },
            { name: "severity_level", type: "string" },
          ],
        },
      }),

      // 3. Image HEAD checks
      n("3", "runCode", "Image URL availability checks", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Check image URLs for availability and minimum size
const issues = JSON.parse(vars.feed_issues || "[]");
const imageIssues = [];
let brokenImages = 0;
// In production, this would perform HEAD requests; here we validate URL patterns
const products = JSON.parse(vars.webhook_payload || "[]");
products.forEach(p => {
  const imgUrl = p.image_link || p.main_image || "";
  if (!imgUrl.match(/^https:\\/\\//)) {
    imageIssues.push({ product_id: p.id, issue: "Non-HTTPS image URL", severity: "critical" });
    brokenImages++;
  }
  if (imgUrl && !imgUrl.match(/\\.(jpg|jpeg|png|webp)/i)) {
    imageIssues.push({ product_id: p.id, issue: "Unsupported image format", severity: "warning" });
  }
});
const allIssues = issues.concat(imageIssues);
return { all_issues: JSON.stringify(allIssues.slice(0, 100)), broken_images: brokenImages, image_issues_count: imageIssues.length };`,
          outputVariables: [
            { name: "all_issues", type: "string" },
            { name: "broken_images", type: "number" },
            { name: "image_issues_count", type: "number" },
          ],
        },
      }),

      // 4. Platform rule packs (AI analysis)
      aiNode(
        "4",
        "AI platform rule analysis",
        760,
        "You are a feed compliance specialist. Analyze the diagnostic issues list and categorize by platform, severity, and recommended fix priority. Generate a ranked report with the most impactful fixes first. Respond JSON: {ranked_report: string, top_priority: string, platforms_affected: string, fix_estimate_hours: number, feed_health_score: number}",
        "Feed issues: {all_issues}. Critical: {critical_count}, Warnings: {warning_count}, Broken images: {broken_images}. Total products: {total_products}.",
        [
          { field: "ranked_report", as: "ranked_report", type: "string" },
          { field: "top_priority", as: "top_priority", type: "string" },
          {
            field: "platforms_affected",
            as: "platforms_affected",
            type: "string",
          },
          {
            field: "fix_estimate_hours",
            as: "fix_estimate_hours",
            type: "number",
          },
          {
            field: "feed_health_score",
            as: "feed_health_score",
            type: "number",
          },
        ],
      ),

      // 5. Critical vs warning condition
      n("5", "condition", "Critical issues found?", 1000, {
        conditionData: [
          {
            variable: "severity_level",
            value: "critical",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 6. Ranked diagnostics report generation (AI)
      aiNode(
        "6",
        "AI diagnostics summary report",
        1240,
        "You are a feed operations report writer. Produce a concise executive summary of the feed health diagnostics suitable for email. Include: overall health score, critical issue count, top 5 fixes ranked by impact, affected platforms, and estimated fix time. Respond JSON: {report_subject: string, report_body: string, action_items: string}",
        "Ranked report: {ranked_report}. Health score: {feed_health_score}/100. Critical: {critical_count}. Warnings: {warning_count}. Images broken: {broken_images}. Platforms: {platforms_affected}. Top priority: {top_priority}. Fix estimate: {fix_estimate_hours}h.",
        [
          { field: "report_subject", as: "report_subject", type: "string" },
          { field: "report_body", as: "report_body", type: "string" },
          { field: "action_items", as: "action_items", type: "string" },
        ],
      ),

      // 7. Merchandising email
      emailNode(
        "7",
        "Email merchandising: Feed diagnostics",
        1480,
        "{merchandising_email}",
        "{report_subject}",
        "{report_body}\\n\\nAction Items:\\n{action_items}\\n\\nFeed Health Score: {feed_health_score}/100\\nCritical Issues: {critical_count}\\nWarnings: {warning_count}\\nBroken Images: {broken_images}",
      ),

      // 8. Feed Slack alert
      slackNode(
        "8",
        "Slack: Feed diagnostics alert",
        1720,
        ":bar_chart: Product Feed Diagnostics\\nHealth Score: {feed_health_score}/100\\nSeverity: {severity_level}\\nCritical: {critical_count} | Warnings: {warning_count}\\nBroken images: {broken_images}\\nPlatforms affected: {platforms_affected}\\nTop priority: {top_priority}\\nFix estimate: {fix_estimate_hours}h",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"), // critical -> full report
      e("5", "6", "output_2"), // warning/clean -> still generate report
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 475. PRODUCT FEEDBACK COLLECTOR (9 nodes)
  // ===================================================================

  "Product Feedback Collector": {
    summary:
      "Multi-source feedback ingestion with AI classification and trend analysis. Ingests feedback from multiple sources, normalizes via runCode, deduplicates entries, AI classifies sentiment and themes, urgency routing escalates critical issues to Slack, stores all feedback in Sheets, sends weekly trend digests via email, and alerts on ingestion failures.",
    nodes: [
      trigger("Webhook: Multi-source feedback ingestion", "webhook_app", {
        source: "Feedback Platforms",
        event: "feedback.received",
        howItStarts:
          "Feedback arrives from reviews, surveys, support tickets, social media, or NPS — all funneled through a single webhook for unified processing",
        setup:
          "Configure webhooks from review platforms (Trustpilot, Yotpo), survey tools (Outgrow Forms), support (Zendesk), and social listening tools to POST to the workflow URL.",
        inputFields: [],
      }),

      // 2. Normalization (runCode)
      n("2", "runCode", "Normalize feedback format", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Normalize feedback from multiple sources into a standard format
const payload = JSON.parse(vars.webhook_payload || "{}");
const source = payload.source || payload.platform || "unknown";
const sourceMap = {
  trustpilot: { text: payload.text || payload.review_text, rating: payload.stars, author: payload.consumer_name, product: payload.product_name },
  yotpo: { text: payload.content, rating: payload.score, author: payload.user?.display_name, product: payload.product_title },
  outgrow-forms: { text: payload.answers?.[0]?.text, rating: payload.answers?.[1]?.number, author: payload.hidden?.email, product: payload.hidden?.product },
  zendesk: { text: payload.description, rating: null, author: payload.requester?.email, product: payload.custom_fields?.product },
  default: { text: payload.text || payload.message || payload.content, rating: payload.rating, author: payload.email || payload.author, product: payload.product }
};
const mapped = sourceMap[source] || sourceMap.default;
const normalized = {
  feedback_text: String(mapped.text || "").trim(),
  rating: mapped.rating ? Number(mapped.rating) : null,
  author: String(mapped.author || "anonymous"),
  product: String(mapped.product || "unknown"),
  source: source,
  received_at: new Date().toISOString(),
  feedback_id: payload.id || payload.feedback_id || Date.now().toString(),
  ingestion_ok: mapped.text ? "yes" : "no"
};
return normalized;`,
          outputVariables: [
            { name: "feedback_text", type: "string" },
            { name: "rating", type: "number" },
            { name: "author", type: "string" },
            { name: "product", type: "string" },
            { name: "source", type: "string" },
            { name: "received_at", type: "string" },
            { name: "feedback_id", type: "string" },
            { name: "ingestion_ok", type: "string" },
          ],
        },
      }),

      // 3. Dedup check
      n("3", "runCode", "Deduplication check", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Simple dedup check based on text hash and recent window
const text = vars.feedback_text || "";
const hash = text.split("").reduce((h, c) => ((h << 5) - h + c.charCodeAt(0)) | 0, 0).toString(36);
const feedbackId = vars.feedback_id || "";
const isDuplicate = false; // In production, check against a dedup store
const dedup_key = hash + "_" + feedbackId;
return { dedup_key, is_duplicate: isDuplicate ? "yes" : "no", text_length: text.length };`,
          outputVariables: [
            { name: "dedup_key", type: "string" },
            { name: "is_duplicate", type: "string" },
            { name: "text_length", type: "number" },
          ],
        },
      }),

      // 4. AI sentiment/theme classification
      aiNode(
        "4",
        "AI sentiment & theme classification",
        760,
        "You are a product feedback analyst. Classify the feedback by sentiment (positive, neutral, negative), urgency (low, medium, high, critical), and themes (quality, usability, pricing, shipping, support, feature_request, bug_report). Extract specific product mentions and actionable insights. Respond JSON: {sentiment: string, urgency: string, themes: string[], primary_theme: string, actionable_insight: string, product_mention: string, requires_response: boolean}",
        "Feedback from {source} about {product} (rating: {rating}): {feedback_text}",
        [
          { field: "sentiment", as: "feedback_sentiment", type: "string" },
          { field: "urgency", as: "feedback_urgency", type: "string" },
          { field: "primary_theme", as: "primary_theme", type: "string" },
          { field: "themes", as: "feedback_themes", type: "string" },
          {
            field: "actionable_insight",
            as: "actionable_insight",
            type: "string",
          },
          {
            field: "requires_response",
            as: "requires_response",
            type: "string",
          },
        ],
      ),

      // 5. Urgency condition
      n("5", "condition", "Critical or high urgency?", 1000, {
        conditionData: [
          {
            variable: "feedback_urgency",
            value: "critical",
            comparison_operator: "EQUAL TO",
            logical_operator: "OR",
          },
          {
            variable: "feedback_urgency",
            value: "high",
            comparison_operator: "EQUAL TO",
            logical_operator: "OR",
          },
        ],
      }),

      // 6. Support/QA escalation (Slack) — True
      slackNode(
        "6",
        "Slack: Urgent feedback escalation",
        1240,
        ":rotating_light: Urgent Product Feedback\\nProduct: {product}\\nSource: {source}\\nSentiment: {feedback_sentiment} | Urgency: {feedback_urgency}\\nTheme: {primary_theme}\\nAuthor: {author} (Rating: {rating})\\nInsight: {actionable_insight}\\nFeedback: {feedback_text}",
      ),

      // 7. Feedback database store (Sheets)
      sheetsNode(
        "7",
        "Log feedback to Sheets",
        1480,
        "ProductFeedback",
        '[["{feedback_id}","{received_at}","{source}","{product}","{author}","{rating}","{feedback_sentiment}","{feedback_urgency}","{primary_theme}","{feedback_themes}","{actionable_insight}","{dedup_key}"]]',
      ),

      // 8. Weekly trend digest (email)
      emailNode(
        "8",
        "Email weekly feedback digest",
        1720,
        "{product_team_email}",
        "Weekly Product Feedback Digest - {product}",
        "Product Feedback Summary\\n\\nProduct: {product}\\nLatest feedback sentiment: {feedback_sentiment}\\nTop theme: {primary_theme}\\nActionable insight: {actionable_insight}\\n\\nThis is part of the ongoing feedback collection. See the full Sheets log for trends and details.",
      ),

      // 9. Ingestion failure alert
      slackNode(
        "9",
        "Slack: Ingestion failure alert",
        1240,
        ":x: Feedback Ingestion Failure\\nFeedback ID: {feedback_id}\\nSource: {source}\\nIngestion status: {ingestion_ok}\\nText length: {text_length}\\nPlease investigate the source integration.",
        400,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"), // critical/high -> Slack escalation
      e("5", "7", "output_2"), // normal -> straight to Sheets
      e("6", "7"),
      e("7", "8"),
      e("8", "9"), // chain to failure alert monitoring
    ],
  },

  // ===================================================================
  // 476. PRODUCT IMAGE QUALITY CHECKER (8 nodes)
  // ===================================================================

  "Product Image Quality Checker": {
    summary:
      "Validates product images against platform compliance requirements. Image upload triggers metadata inspection via runCode, platform minimum validation checks dimensions and format, AI visual analysis assesses composition and quality, routing separates passing from failing images, generates compliance reports, emails uploaders, and alerts the content quality team via Slack.",
    nodes: [
      trigger("Webhook: Product image uploaded", "webhook_app", {
        source: "Asset Manager",
        event: "image.uploaded",
        howItStarts:
          "A product image is uploaded to the asset management system, triggering automated quality and compliance checks for all marketplace platforms",
        setup:
          "Connect your DAM or asset manager webhook for image upload events. Configure platform-specific image requirements.",
        inputFields: [],
      }),

      // 2. Metadata inspection (runCode)
      n("2", "runCode", "Image metadata inspection", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Extract and validate image metadata
const payload = JSON.parse(vars.webhook_payload || "{}");
const imageUrl = payload.url || payload.image_url || payload.src || "";
const fileName = payload.filename || payload.name || imageUrl.split("/").pop() || "unknown";
const fileSize = payload.size || payload.file_size || 0;
const width = payload.width || payload.dimensions?.width || 0;
const height = payload.height || payload.dimensions?.height || 0;
const format = (fileName.split(".").pop() || "").toLowerCase();
const productId = payload.product_id || payload.sku || "";
const uploadedBy = payload.uploaded_by || payload.user_email || "";
const aspectRatio = height > 0 ? (width / height).toFixed(2) : "0";
return {
  image_url: imageUrl, file_name: fileName, file_size_kb: Math.round(fileSize / 1024),
  img_width: width, img_height: height, img_format: format, aspect_ratio: aspectRatio,
  image_product_id: productId, uploader_email: uploadedBy
};`,
          outputVariables: [
            { name: "image_url", type: "string" },
            { name: "file_name", type: "string" },
            { name: "file_size_kb", type: "number" },
            { name: "img_width", type: "number" },
            { name: "img_height", type: "number" },
            { name: "img_format", type: "string" },
            { name: "aspect_ratio", type: "string" },
            { name: "image_product_id", type: "string" },
            { name: "uploader_email", type: "string" },
          ],
        },
      }),

      // 3. Platform minimums validation (runCode)
      n("3", "runCode", "Platform minimum validation", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Validate against platform-specific image requirements
const w = parseInt(vars.img_width) || 0;
const h = parseInt(vars.img_height) || 0;
const fmt = vars.img_format || "";
const sizeKb = parseInt(vars.file_size_kb) || 0;
const platforms = {
  google: { minW: 100, minH: 100, maxKb: 16384, formats: ["jpg","jpeg","png","gif","bmp","tif","tiff","webp"] },
  amazon: { minW: 1000, minH: 1000, maxKb: 10240, formats: ["jpg","jpeg","png","gif","tif","tiff"] },
  meta: { minW: 500, minH: 500, maxKb: 8192, formats: ["jpg","jpeg","png"] },
  shopify: { minW: 800, minH: 800, maxKb: 20480, formats: ["jpg","jpeg","png","gif","webp"] }
};
const results = {};
let passCount = 0, failCount = 0;
Object.entries(platforms).forEach(([p, r]) => {
  const dimOk = w >= r.minW && h >= r.minH;
  const sizeOk = sizeKb <= r.maxKb;
  const fmtOk = r.formats.includes(fmt);
  const pass = dimOk && sizeOk && fmtOk;
  results[p] = { pass, dimOk, sizeOk, fmtOk };
  if (pass) passCount++; else failCount++;
});
const quality_status = failCount === 0 ? "pass" : "fail";
return { platform_results: JSON.stringify(results), platforms_pass: passCount, platforms_fail: failCount, quality_status };`,
          outputVariables: [
            { name: "platform_results", type: "string" },
            { name: "platforms_pass", type: "number" },
            { name: "platforms_fail", type: "number" },
            { name: "quality_status", type: "string" },
          ],
        },
      }),

      // 4. AI visual analysis
      aiNode(
        "4",
        "AI visual quality analysis",
        760,
        "You are an image quality analyst for e-commerce. Evaluate the image metadata for marketplace compliance. Check background quality (white/clean preferred), aspect ratio suitability, resolution adequacy, and overall professional quality. Score 0-100. Respond JSON: {quality_score: number, background_assessment: string, composition_notes: string, improvement_suggestions: string, marketplace_ready: boolean}",
        "Image: {file_name} ({img_format}, {img_width}x{img_height}, {file_size_kb}KB). Aspect ratio: {aspect_ratio}. Platform results: {platform_results}. Platforms passing: {platforms_pass}/{platforms_fail} fail. Product: {image_product_id}.",
        [
          { field: "quality_score", as: "quality_score", type: "number" },
          {
            field: "background_assessment",
            as: "background_assessment",
            type: "string",
          },
          {
            field: "composition_notes",
            as: "composition_notes",
            type: "string",
          },
          {
            field: "improvement_suggestions",
            as: "improvement_suggestions",
            type: "string",
          },
          {
            field: "marketplace_ready",
            as: "marketplace_ready",
            type: "string",
          },
        ],
      ),

      // 5. Quality condition
      n("5", "condition", "Image passes all platforms?", 1000, {
        conditionData: [
          {
            variable: "quality_status",
            value: "pass",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 6. Compliance report generation (both paths converge)
      aiNode(
        "6",
        "AI compliance report",
        1240,
        "You are a content compliance reporter. Generate a concise image quality report for the uploader. Include pass/fail by platform, quality score, specific issues, and remediation steps. Respond JSON: {report_subject: string, report_body: string, remediation_steps: string}",
        "Image: {file_name} for product {image_product_id}. Quality score: {quality_score}/100. Status: {quality_status}. Platform results: {platform_results}. Background: {background_assessment}. Composition: {composition_notes}. Suggestions: {improvement_suggestions}.",
        [
          { field: "report_subject", as: "img_report_subject", type: "string" },
          { field: "report_body", as: "img_report_body", type: "string" },
          {
            field: "remediation_steps",
            as: "remediation_steps",
            type: "string",
          },
        ],
      ),

      // 7. Uploader email
      emailNode(
        "7",
        "Email uploader: Image quality report",
        1480,
        "{uploader_email}",
        "{img_report_subject}",
        "{img_report_body}\\n\\nRemediation Steps:\\n{remediation_steps}\\n\\nImage: {file_name} ({img_width}x{img_height}, {img_format})\\nQuality Score: {quality_score}/100\\nPlatforms Passing: {platforms_pass} | Failing: {platforms_fail}",
      ),

      // 8. Content quality Slack alert
      slackNode(
        "8",
        "Slack: Content quality alert",
        1720,
        ":camera: Image Quality Check\\nFile: {file_name}\\nProduct: {image_product_id}\\nStatus: {quality_status}\\nQuality Score: {quality_score}/100\\nPlatforms: {platforms_pass} pass / {platforms_fail} fail\\nUploader: {uploader_email}\\nBackground: {background_assessment}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"), // pass -> compliance report
      e("5", "6", "output_2"), // fail -> compliance report (same, different content)
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 477. PRODUCT LAUNCH ANNOUNCEMENT (9 nodes)
  // ===================================================================

  "Product Launch Announcement": {
    summary:
      "Orchestrates multi-channel product launch announcements with audience segmentation. Launch event triggers product/stock pull from Shopify, AI generates launch copy, runCode segments the audience, VIP early-access emails go first, general launch emails follow, SMS reaches opted-in customers, internal Slack notifies the team, and performance is logged to Sheets.",
    nodes: [
      trigger("Webhook: Product launch event", "webhook_app", {
        source: "Product Management",
        event: "product.launch",
        howItStarts:
          "A product is marked for launch, triggering a coordinated multi-channel announcement to segmented audiences",
        setup:
          "Configure product management system webhook. Set up Shopify API, email service, SMS (Twilio), and Slack integrations.",
        inputFields: [],
      }),

      // 2. Product/stock pull (Shopify)
      aiNode(
        "2",
        "AI extract launch data",
        280,
        "You are a launch data parser. Extract the product ID, launch date, target audience info, and any special launch parameters from the webhook payload regardless of format. Respond JSON: {product_id: string, launch_date: string, launch_name: string, target_segments: string, early_access_hours: number, discount_code: string, launch_channel: string}",
        "{webhook_payload}",
        [
          { field: "product_id", as: "launch_product_id", type: "string" },
          { field: "launch_date", as: "launch_date", type: "string" },
          { field: "launch_name", as: "launch_name", type: "string" },
          { field: "target_segments", as: "target_segments", type: "string" },
          {
            field: "early_access_hours",
            as: "early_access_hours",
            type: "number",
          },
          { field: "discount_code", as: "discount_code", type: "string" },
        ],
      ),

      // 3. Shopify product data
      shopifyNode(
        "3",
        "Pull product details from Shopify",
        520,
        "GET",
        "products/{launch_product_id}.json",
        [],
        [
          {
            name: "product.title",
            customName: "product_title",
            type: "string",
          },
          {
            name: "product.body_html",
            customName: "product_description",
            type: "string",
          },
          {
            name: "product.variants[0].price",
            customName: "product_price",
            type: "string",
          },
          {
            name: "product.variants[0].inventory_quantity",
            customName: "stock_level",
            type: "number",
          },
          {
            name: "product.images[0].src",
            customName: "product_image",
            type: "string",
          },
        ],
      ),

      // 4. AI launch copy generation
      aiNode(
        "4",
        "AI launch copy generation",
        760,
        "You are a product launch copywriter. Create compelling launch copy for multiple channels: email subject + body for VIPs, email subject + body for general audience, and SMS message. Use the product details and pricing. Make VIP copy exclusive and urgent. Respond JSON: {vip_subject: string, vip_body: string, general_subject: string, general_body: string, sms_message: string, social_headline: string}",
        "Product: {product_title}. Price: ${product_price}. Description: {product_description}. Launch: {launch_name} on {launch_date}. Discount code: {discount_code}. Stock: {stock_level} units.",
        [
          { field: "vip_subject", as: "vip_subject", type: "string" },
          { field: "vip_body", as: "vip_body", type: "string" },
          { field: "general_subject", as: "general_subject", type: "string" },
          { field: "general_body", as: "general_body", type: "string" },
          { field: "sms_message", as: "sms_message", type: "string" },
        ],
      ),

      // 5. Audience segmentation (runCode)
      n("5", "runCode", "Audience segmentation", 1000, {
        runCodeDetails: {
          language: "javascript",
          code: `// Segment audience for launch communications
const segments = vars.target_segments || "all";
const vipList = "{vip_segment_email}"; // Placeholder for CRM segment
const generalList = "{general_segment_email}";
const smsOptIn = "{sms_optin_list}";
const segmented = {
  vip_email: vipList,
  general_email: generalList,
  sms_list: smsOptIn,
  vip_send_time: new Date().toISOString(),
  general_delay_hours: parseInt(vars.early_access_hours) || 24,
  segments_targeted: segments
};
return segmented;`,
          outputVariables: [
            { name: "vip_email", type: "string" },
            { name: "general_email", type: "string" },
            { name: "sms_list", type: "string" },
            { name: "general_delay_hours", type: "number" },
            { name: "segments_targeted", type: "string" },
          ],
        },
      }),

      // 6. VIP early-access email
      emailNode(
        "6",
        "VIP early-access email",
        1240,
        "{vip_email}",
        "{vip_subject}",
        "{vip_body}",
      ),

      // 7. General launch email
      emailNode(
        "7",
        "General launch email",
        1480,
        "{general_email}",
        "{general_subject}",
        "{general_body}",
      ),

      // 8. SMS to opted-in (Twilio)
      smsNode(
        "8",
        "SMS launch notification",
        1720,
        "{sms_list}",
        "{sms_message}",
      ),

      // 9. Internal Slack notification + performance logging (Sheets)
      slackNode(
        "9",
        "Slack: Launch notification + logging",
        1960,
        ":rocket: Product Launch Executed\\nProduct: {product_title}\\nLaunch: {launch_name}\\nDate: {launch_date}\\nPrice: ${product_price}\\nStock: {stock_level}\\nSegments: {segments_targeted}\\nVIP early access: {early_access_hours}h\\nDiscount: {discount_code}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
      e("8", "9"),
    ],
  },

  // ===================================================================
  // 478. PRODUCT LAUNCH COUNTDOWN CAMPAIGN (10 nodes)
  // ===================================================================

  "Product Launch Countdown Campaign": {
    summary:
      "Automated multi-touch countdown campaign from T-7 to launch day. Campaign planner triggers countdown schedule generation via runCode, AI creates the full message suite, then sequential emails fire at T-7 (teaser), T-3 (reveal), T-1 (reminder), and T-0 (launch + SMS), followed by 24hr performance aggregation, marketing ops Slack report, and stock-exhaustion suppression logic.",
    nodes: [
      trigger("Manual: Campaign planner trigger", "manual", {
        howItStarts:
          "Marketing team initiates a product launch countdown campaign, setting the launch date and product details",
        setup:
          "Enter the product ID, launch date, and campaign parameters. Configure email service, SMS (Twilio), and Slack.",
        inputFields: [
          { name: "product_id", type: "string", example: "prod_12345" },
          { name: "launch_date", type: "string", example: "2026-04-15" },
          {
            name: "product_name",
            type: "string",
            example: "Ultra Pro Headphones",
          },
          { name: "product_price", type: "string", example: "299.99" },
          {
            name: "campaign_audience",
            type: "string",
            example: "all_subscribers",
          },
        ],
      }),

      // 2. Countdown schedule (runCode)
      n("2", "runCode", "Generate countdown schedule", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Generate countdown email schedule
const launchDate = new Date(vars.launch_date || Date.now());
const t7 = new Date(launchDate); t7.setDate(t7.getDate() - 7);
const t3 = new Date(launchDate); t3.setDate(t3.getDate() - 3);
const t1 = new Date(launchDate); t1.setDate(t1.getDate() - 1);
const t0 = new Date(launchDate);
return {
  t7_date: t7.toISOString().split("T")[0],
  t3_date: t3.toISOString().split("T")[0],
  t1_date: t1.toISOString().split("T")[0],
  t0_date: t0.toISOString().split("T")[0],
  campaign_id: "countdown_" + (vars.product_id || "unknown") + "_" + Date.now(),
  days_until_launch: Math.ceil((launchDate.getTime() - Date.now()) / 86400000)
};`,
          outputVariables: [
            { name: "t7_date", type: "string" },
            { name: "t3_date", type: "string" },
            { name: "t1_date", type: "string" },
            { name: "t0_date", type: "string" },
            { name: "campaign_id", type: "string" },
            { name: "days_until_launch", type: "number" },
          ],
        },
      }),

      // 3. AI message suite
      aiNode(
        "3",
        "AI countdown message suite",
        520,
        "You are a product launch campaign copywriter. Create a 4-email countdown series plus an SMS for launch day. Each email needs a subject and body. T-7: mysterious teaser. T-3: product reveal with key features. T-1: urgency/reminder with countdown. T-0: launch announcement with CTA. SMS: short launch alert. Respond JSON: {t7_subject: string, t7_body: string, t3_subject: string, t3_body: string, t1_subject: string, t1_body: string, t0_subject: string, t0_body: string, sms_launch: string}",
        "Product: {product_name}. Price: ${product_price}. Launch: {launch_date}. Audience: {campaign_audience}. Campaign ID: {campaign_id}.",
        [
          { field: "t7_subject", as: "t7_subject", type: "string" },
          { field: "t7_body", as: "t7_body", type: "string" },
          { field: "t3_subject", as: "t3_subject", type: "string" },
          { field: "t3_body", as: "t3_body", type: "string" },
          { field: "t1_subject", as: "t1_subject", type: "string" },
          { field: "t1_body", as: "t1_body", type: "string" },
          { field: "t0_subject", as: "t0_subject", type: "string" },
          { field: "t0_body", as: "t0_body", type: "string" },
          { field: "sms_launch", as: "sms_launch", type: "string" },
        ],
      ),

      // 4. T-7 teaser email
      emailNode(
        "4",
        "T-7: Teaser email",
        760,
        "{campaign_audience}",
        "{t7_subject}",
        "{t7_body}",
      ),

      // 5. T-3 reveal email
      emailNode(
        "5",
        "T-3: Product reveal email",
        1000,
        "{campaign_audience}",
        "{t3_subject}",
        "{t3_body}",
      ),

      // 6. T-1 reminder email
      emailNode(
        "6",
        "T-1: Reminder email",
        1240,
        "{campaign_audience}",
        "{t1_subject}",
        "{t1_body}",
      ),

      // 7. T-0 launch email + SMS
      emailNode(
        "7",
        "T-0: Launch email",
        1480,
        "{campaign_audience}",
        "{t0_subject}",
        "{t0_body}",
      ),

      // 8. SMS launch notification
      smsNode(
        "8",
        "T-0: Launch SMS",
        1720,
        "{campaign_audience}",
        "{sms_launch}",
      ),

      // 9. Marketing ops Slack report
      slackNode(
        "9",
        "Slack: Marketing ops report",
        1960,
        ":chart_with_upwards_trend: Launch Countdown Complete\\nCampaign: {campaign_id}\\nProduct: {product_name}\\nLaunch date: {launch_date}\\nPrice: ${product_price}\\nEmails sent: T-7, T-3, T-1, T-0\\nSMS sent: Yes\\nDays in campaign: 7\\nAudience: {campaign_audience}",
      ),

      // 10. Stock-exhaustion suppression
      n("10", "runCode", "Stock-exhaustion suppression check", 2200, {
        runCodeDetails: {
          language: "javascript",
          code: `// Check if stock is exhausted and suppress further sends
const campaignId = vars.campaign_id || "";
const productName = vars.product_name || "";
const suppressionApplied = false; // In production, check real-time stock
const result = {
  suppression_check: "complete",
  campaign_id: campaignId,
  product_name: productName,
  stock_exhausted: suppressionApplied ? "yes" : "no",
  action: suppressionApplied ? "suppressed_future_sends" : "campaign_complete"
};
return result;`,
          outputVariables: [
            { name: "suppression_check", type: "string" },
            { name: "stock_exhausted", type: "string" },
            { name: "action", type: "string" },
          ],
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
      e("8", "9"),
      e("9", "10"),
    ],
  },

  // ===================================================================
  // 479. PRODUCT LISTING COMPLIANCE CHECK (8 nodes)
  // ===================================================================

  "Product Listing Compliance Check": {
    summary:
      "Nightly compliance scanner for product listing text and claims. Scheduled scan triggers runCode text/claims analysis, AI detects policy risks, critical vs minor routing prioritizes actions, generates detailed compliance reports, emails merchandising, alerts Slack on critical violations, and logs false positives to Sheets for model improvement.",
    nodes: [
      trigger("Schedule: Nightly compliance scan", "schedule", {
        schedule: "0 2 * * *",
        howItStarts:
          "Every night at 2 AM, the workflow scans all product listings for regulatory compliance issues, prohibited claims, and policy violations",
        setup:
          "Configure Shopify API for catalog access. Set up compliance rule sets per jurisdiction. Configure Slack, email, and Sheets.",
        inputFields: [],
      }),

      // 2. Text/claims scan (runCode)
      n("2", "runCode", "Scan listing text for policy violations", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Scan product listing text for prohibited claims and policy violations
const products = JSON.parse(vars.webhook_payload || "[]");
const prohibitedTerms = {
  health_claims: ["cures","treats","prevents disease","FDA approved","clinically proven","miracle"],
  environmental: ["100% eco","zero carbon","completely sustainable","all natural"],
  pricing: ["lowest price guaranteed","best deal ever","free"],
  legal: ["patented","trademarked","certified"]
};
const violations = [];
let criticalCount = 0, minorCount = 0;
(products.length ? products : []).forEach(p => {
  const text = ((p.title || "") + " " + (p.body_html || "")).toLowerCase();
  Object.entries(prohibitedTerms).forEach(([category, terms]) => {
    terms.forEach(term => {
      if (text.includes(term.toLowerCase())) {
        const severity = ["health_claims","legal"].includes(category) ? "critical" : "minor";
        violations.push({ product_id: p.id, product_title: p.title, category, term, severity });
        if (severity === "critical") criticalCount++; else minorCount++;
      }
    });
  });
});
const compliance_level = criticalCount > 0 ? "critical" : minorCount > 0 ? "minor" : "clean";
return { violations: JSON.stringify(violations.slice(0, 50)), critical_violations: criticalCount, minor_violations: minorCount, total_scanned: products.length, compliance_level };`,
          outputVariables: [
            { name: "violations", type: "string" },
            { name: "critical_violations", type: "number" },
            { name: "minor_violations", type: "number" },
            { name: "total_scanned", type: "number" },
            { name: "compliance_level", type: "string" },
          ],
        },
      }),

      // 3. AI policy risk detection
      aiNode(
        "3",
        "AI policy risk analysis",
        520,
        "You are a product compliance analyst. Review the detected violations and assess actual risk. Some matches may be false positives (e.g., 'patented' in a legitimate patent holder context). For each violation, assign a risk score (1-10) and recommend action: remove, rewrite, review, or dismiss. Respond JSON: {risk_report: string, high_risk_count: number, false_positive_count: number, recommended_actions: string, compliance_summary: string, overall_risk: string}",
        "Violations found: {violations}. Critical: {critical_violations}. Minor: {minor_violations}. Products scanned: {total_scanned}.",
        [
          { field: "risk_report", as: "risk_report", type: "string" },
          { field: "high_risk_count", as: "high_risk_count", type: "number" },
          {
            field: "false_positive_count",
            as: "false_positive_count",
            type: "number",
          },
          {
            field: "recommended_actions",
            as: "recommended_actions",
            type: "string",
          },
          {
            field: "compliance_summary",
            as: "compliance_summary",
            type: "string",
          },
          { field: "overall_risk", as: "overall_risk", type: "string" },
        ],
      ),

      // 4. Critical vs minor condition
      n("4", "condition", "Critical violations present?", 760, {
        conditionData: [
          {
            variable: "compliance_level",
            value: "critical",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 5. Detailed compliance report
      aiNode(
        "5",
        "AI compliance report",
        1000,
        "You are a compliance report writer. Generate a professional compliance report with executive summary, violation details, risk assessment, and remediation timeline. Respond JSON: {report_subject: string, report_body: string, remediation_timeline: string, priority_fixes: string}",
        "Compliance level: {compliance_level}. Risk report: {risk_report}. Summary: {compliance_summary}. High risk: {high_risk_count}. False positives: {false_positive_count}. Actions: {recommended_actions}.",
        [
          {
            field: "report_subject",
            as: "compliance_report_subject",
            type: "string",
          },
          {
            field: "report_body",
            as: "compliance_report_body",
            type: "string",
          },
          {
            field: "remediation_timeline",
            as: "remediation_timeline",
            type: "string",
          },
          { field: "priority_fixes", as: "priority_fixes", type: "string" },
        ],
      ),

      // 6. Merchandising email
      emailNode(
        "6",
        "Email merchandising: Compliance report",
        1240,
        "{merchandising_email}",
        "{compliance_report_subject}",
        "{compliance_report_body}\\n\\nPriority Fixes:\\n{priority_fixes}\\n\\nRemediation Timeline:\\n{remediation_timeline}\\n\\nOverall Risk: {overall_risk}\\nCritical: {critical_violations} | Minor: {minor_violations} | False Positives: {false_positive_count}",
      ),

      // 7. Critical Slack alert (True path)
      slackNode(
        "7",
        "Slack: Critical compliance alert",
        1480,
        ":rotating_light: CRITICAL Compliance Violations Detected\\nLevel: {compliance_level}\\nCritical: {critical_violations} | Minor: {minor_violations}\\nHigh risk: {high_risk_count}\\nOverall risk: {overall_risk}\\nProducts scanned: {total_scanned}\\nPriority fixes: {priority_fixes}\\nImmediate action required.",
      ),

      // 8. False-positive logging (Sheets)
      sheetsNode(
        "8",
        "Log compliance results to Sheets",
        1720,
        "ComplianceLog",
        '[["{compliance_level}","{critical_violations}","{minor_violations}","{high_risk_count}","{false_positive_count}","{overall_risk}","{total_scanned}","{compliance_summary}","{remediation_timeline}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"), // critical -> full compliance report
      e("4", "5", "output_2"), // minor/clean -> still generate report
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 480. PRODUCT PAGE VIEWED MULTIPLE TIMES NO CART (8 nodes)
  // ===================================================================

  "Product Page Viewed Multiple Times No Cart": {
    summary:
      "Re-engages customers who view a product repeatedly without adding to cart. Repeated PDP view triggers product and customer enrichment, AI infers the likely purchase objection, objection routing sends targeted reassurance emails (price, size/fit, quality/reviews), and objection model performance is logged to Sheets.",
    nodes: [
      trigger("Webhook: Repeated product page views", "webhook_app", {
        source: "Analytics / CDP",
        event: "pdp.repeated_view",
        howItStarts:
          "A customer views the same product detail page 3+ times without adding to cart, signaling interest but hesitation",
        setup:
          "Configure CDP or analytics tool to fire a webhook when a user views the same PDP 3+ times within 7 days without cart action.",
        inputFields: [],
      }),

      // 2. Product + customer enrichment (AI extraction)
      aiNode(
        "2",
        "AI extract view & customer data",
        280,
        "You are a behavioral data parser. Extract the customer email, customer name, product ID, product name, product price, view count, days since first view, customer purchase history summary, and browse history from the webhook payload regardless of format. Respond JSON: {customer_email: string, customer_name: string, product_id: string, product_name: string, product_price: string, view_count: number, days_browsing: number, purchase_history: string, customer_segment: string}",
        "{webhook_payload}",
        [
          { field: "customer_email", as: "pdp_customer_email", type: "string" },
          { field: "customer_name", as: "pdp_customer_name", type: "string" },
          { field: "product_name", as: "pdp_product_name", type: "string" },
          { field: "product_price", as: "pdp_product_price", type: "string" },
          { field: "view_count", as: "pdp_view_count", type: "number" },
          { field: "days_browsing", as: "days_browsing", type: "number" },
          { field: "purchase_history", as: "purchase_history", type: "string" },
          { field: "customer_segment", as: "customer_segment", type: "string" },
        ],
      ),

      // 3. AI objection inference
      aiNode(
        "3",
        "AI objection inference",
        520,
        "You are a purchase objection analyst. Given the customer's browsing behavior (product, view count, days browsing, purchase history, segment), infer the most likely objection preventing purchase: 'price' (price too high, comparing options), 'size_fit' (uncertain about sizing or fit), 'quality' (needs social proof or reviews), or 'timing' (waiting for sale/event). Respond JSON: {primary_objection: string, objection_confidence: number, secondary_objection: string, personalized_hook: string, recommended_incentive: string}",
        "Product: {pdp_product_name} at ${pdp_product_price}. Views: {pdp_view_count} over {days_browsing} days. Purchase history: {purchase_history}. Segment: {customer_segment}.",
        [
          {
            field: "primary_objection",
            as: "primary_objection",
            type: "string",
          },
          {
            field: "objection_confidence",
            as: "objection_confidence",
            type: "number",
          },
          {
            field: "secondary_objection",
            as: "secondary_objection",
            type: "string",
          },
          {
            field: "personalized_hook",
            as: "personalized_hook",
            type: "string",
          },
          {
            field: "recommended_incentive",
            as: "recommended_incentive",
            type: "string",
          },
        ],
      ),

      // 4. Objection routing condition
      n("4", "condition", "Price objection?", 760, {
        conditionData: [
          {
            variable: "primary_objection",
            value: "price",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 5. Price reassurance email (True)
      emailNode(
        "5",
        "Price reassurance email",
        1000,
        "{pdp_customer_email}",
        "Still thinking about {pdp_product_name}? Here's why it's worth it",
        "Hi {pdp_customer_name},\\n\\nWe noticed you've been eyeing the {pdp_product_name}. {personalized_hook}\\n\\nHere's what makes it a great value at ${pdp_product_price}:\\n- Premium quality that lasts\\n- {recommended_incentive}\\n- Free returns within 30 days\\n\\nStill on the fence? Reply to this email and we'll help you decide.\\n\\n[Shop Now] [Compare Options]",
      ),

      // 6. Size/fit email (False -> secondary check)
      emailNode(
        "6",
        "Size/fit reassurance email",
        1000,
        "{pdp_customer_email}",
        "Not sure about the fit? We've got you covered - {pdp_product_name}",
        "Hi {pdp_customer_name},\\n\\nWe see you're interested in the {pdp_product_name}. {personalized_hook}\\n\\nUnsure about sizing?\\n- Check our detailed size guide\\n- Free exchanges on all orders\\n- Our fit experts are here to help\\n\\n{recommended_incentive}\\n\\n[View Size Guide] [Chat with Fit Expert] [Shop Now]",
        400,
      ),

      // 7. Quality/reviews email (catch-all)
      emailNode(
        "7",
        "Quality & reviews email",
        1240,
        "{pdp_customer_email}",
        "See what others say about {pdp_product_name}",
        "Hi {pdp_customer_name},\\n\\n{personalized_hook}\\n\\nThe {pdp_product_name} is one of our top-rated products. Here's what customers love:\\n- Verified 4.5+ star reviews\\n- Award-winning quality\\n- 30-day money-back guarantee\\n\\n{recommended_incentive}\\n\\n[Read Reviews] [Shop Now]",
      ),

      // 8. Objection model logging (Sheets)
      sheetsNode(
        "8",
        "Log objection model to Sheets",
        1480,
        "ObjectionModel",
        '[["{pdp_customer_email}","{pdp_product_name}","{pdp_product_price}","{pdp_view_count}","{days_browsing}","{primary_objection}","{objection_confidence}","{secondary_objection}","{recommended_incentive}","{customer_segment}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"), // price objection -> price email
      e("4", "6", "output_2"), // not price -> size/fit email
      e("5", "8"),
      e("6", "7"), // size/fit leads to quality fallback
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 481. PRODUCT Q&A CHATBOT (7 nodes)
  // ===================================================================

  "Product Q&A Chatbot": {
    summary:
      "Widget-powered Q&A chatbot with knowledge grounding and public seeding. Q&A widget triggers Pinecone knowledge search, confidence routing determines answer quality, AI generates grounded answers, high-quality Q&A pairs are proposed for public seeding, low confidence escalates to Zendesk, and product QA is alerted via Slack.",
    nodes: [
      trigger("Widget trigger: Product Q&A question", "webhook_form", {
        source: "Q&A Widget",
        event: "question.submitted",
        howItStarts:
          "A customer submits a question through the product page Q&A widget, seeking product-specific information",
        setup:
          "Embed the Q&A widget on product pages. Configure Pinecone index with product knowledge base. Set up Zendesk and Slack.",
        inputFields: [],
      }),

      // 2. Product knowledge search (Pinecone)
      aiNode(
        "2",
        "AI parse Q&A question",
        280,
        "You are a Q&A question parser. Extract the customer question, product ID, product name, and customer identifier from the widget submission regardless of format. Generate a search query optimized for vector similarity. Respond JSON: {question: string, search_query: string, product_id: string, product_name: string, customer_id: string, question_type: string}",
        "{webhook_payload}",
        [
          { field: "question", as: "qa_question", type: "string" },
          { field: "search_query", as: "qa_search_query", type: "string" },
          { field: "product_id", as: "qa_product_id", type: "string" },
          { field: "product_name", as: "qa_product_name", type: "string" },
          { field: "customer_id", as: "qa_customer_id", type: "string" },
          { field: "question_type", as: "qa_question_type", type: "string" },
        ],
      ),

      // 3. Confidence condition (Pinecone search + inline score check)
      pineconeNode(
        "3",
        "Knowledge base search (Pinecone)",
        520,
        "{qa_search_query}",
        "product_knowledge",
        [
          {
            name: "matches[0].metadata.answer",
            customName: "qa_top_answer",
            type: "string",
          },
          {
            name: "matches[0].score",
            customName: "qa_match_score",
            type: "number",
          },
          {
            name: "matches[0].metadata.source",
            customName: "qa_source",
            type: "string",
          },
        ],
      ),

      // 4. Confidence condition
      n("4", "condition", "Match score >= 0.78?", 760, {
        conditionData: [
          {
            variable: "qa_match_score",
            value: "0.78",
            comparison_operator: "GREATER THAN OR EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 5. AI grounded answer + public seeding proposal (True)
      aiNode(
        "5",
        "AI grounded Q&A answer",
        1000,
        "You are a product Q&A expert. Given the customer's question and the retrieved knowledge base match, provide a clear, helpful, and accurate answer. If this is a commonly asked question with a high-quality answer, propose it for public Q&A seeding. Respond JSON: {answer: string, propose_public: boolean, public_question: string, public_answer: string, answer_quality: string}",
        "Question: {qa_question}. Product: {qa_product_name}. KB match (score {qa_match_score}): {qa_top_answer}. Source: {qa_source}. Question type: {qa_question_type}.",
        [
          { field: "answer", as: "qa_response", type: "string" },
          { field: "propose_public", as: "propose_public", type: "string" },
          { field: "public_question", as: "public_question", type: "string" },
          { field: "public_answer", as: "public_answer", type: "string" },
        ],
      ),

      // 6. Zendesk escalation (False)
      zendeskNode(
        "6",
        "Escalate to Zendesk",
        1000,
        "Product Q&A - {qa_product_name}: {qa_question}",
        "Customer Q&A could not be auto-answered.\\n\\nProduct: {qa_product_name} (ID: {qa_product_id})\\nQuestion: {qa_question}\\nType: {qa_question_type}\\nBest KB match score: {qa_match_score}\\nBest match: {qa_top_answer}",
        "normal",
        [{ name: "ticket.id", customName: "qa_ticket_id", type: "string" }],
        400,
      ),

      // 7. Product QA Slack alert
      slackNode(
        "7",
        "Slack: Product QA alert",
        1240,
        ":speech_balloon: Product Q&A Activity\\nProduct: {qa_product_name}\\nQuestion: {qa_question}\\nType: {qa_question_type}\\nMatch score: {qa_match_score}\\nPublic seeding proposed: {propose_public}\\nTicket: {qa_ticket_id}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"), // high confidence -> grounded answer
      e("4", "6", "output_2"), // low confidence -> Zendesk
      e("5", "7"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 482. PRODUCT RECALL LOGISTICS BOT (9 nodes)
  // ===================================================================

  "Product Recall Logistics Bot": {
    summary:
      "Handles recall support from intake to fulfillment. Recall support trigger verifies affected SKUs, AI classifies customer intent (return, replacement, info), routes by intent, generates return labels, creates replacement orders in Shopify, delivers responses, escalates safety concerns to Slack, and logs everything to Sheets for compliance.",
    nodes: [
      trigger("Chat trigger: Recall support request", "webhook_form", {
        source: "Chat Widget / Support",
        event: "message.received",
        howItStarts:
          "A customer contacts support regarding a product recall, seeking return instructions, replacement, or information about their affected product",
        setup:
          "Connect chat widget and support channels. Configure Shopify for order lookup and replacement creation. Set up shipping API for return labels.",
        inputFields: [],
      }),

      // 2. Affected SKU verification
      aiNode(
        "2",
        "AI extract recall support data",
        280,
        "You are a recall support data parser. Extract the customer identifier, order number, product SKU, product name, purchase date, and the nature of their recall inquiry from the message regardless of format. Check against known recall SKU list. Respond JSON: {customer_email: string, customer_name: string, order_id: string, product_sku: string, product_name: string, purchase_date: string, is_affected: boolean, recall_id: string, inquiry_type: string}",
        "{webhook_payload}",
        [
          {
            field: "customer_email",
            as: "recall_customer_email",
            type: "string",
          },
          {
            field: "customer_name",
            as: "recall_customer_name",
            type: "string",
          },
          { field: "order_id", as: "recall_order_id", type: "string" },
          { field: "product_sku", as: "recall_sku", type: "string" },
          { field: "product_name", as: "recall_product_name", type: "string" },
          { field: "is_affected", as: "is_affected", type: "string" },
          { field: "recall_id", as: "recall_id", type: "string" },
          { field: "inquiry_type", as: "recall_inquiry_type", type: "string" },
        ],
      ),

      // 3. AI intent classification
      aiNode(
        "3",
        "AI recall intent classification",
        520,
        "You are a recall support classifier. Given the customer's inquiry about a recalled product, classify their intent: 'return' (wants to send product back), 'replacement' (wants a replacement unit), 'information' (wants recall details), or 'safety' (reporting an injury or safety incident). Assess urgency. Respond JSON: {intent: string, urgency: string, safety_concern: boolean, response_template: string, needs_label: boolean, needs_replacement: boolean}",
        "Customer: {recall_customer_name}. Product: {recall_product_name} (SKU: {recall_sku}). Order: {recall_order_id}. Inquiry type: {recall_inquiry_type}. Affected: {is_affected}. Recall ID: {recall_id}.",
        [
          { field: "intent", as: "recall_intent", type: "string" },
          { field: "urgency", as: "recall_urgency", type: "string" },
          { field: "safety_concern", as: "safety_concern", type: "string" },
          { field: "needs_label", as: "needs_label", type: "string" },
          {
            field: "needs_replacement",
            as: "needs_replacement",
            type: "string",
          },
        ],
      ),

      // 4. Intent routing condition
      n("4", "condition", "Needs return label?", 760, {
        conditionData: [
          {
            variable: "needs_label",
            value: "true",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 5. Return label generation (True)
      n("5", "hitApi", "Generate return shipping label", 1000, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.easypost.com/v2/shipments",
          headers: [
            { key: "Authorization", value: "Bearer {easypost_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "shipment",
              value:
                '{"is_return":true,"to_address":{"company":"Returns Center"},"from_address":{"email":"{recall_customer_email}"},"parcel":{"predefined_package":"SmallFlatRateBox"}}',
            },
          ],
          variables: [
            {
              name: "postage_label.label_url",
              customName: "return_label_url",
              type: "string",
            },
            {
              name: "tracking_code",
              customName: "return_tracking",
              type: "string",
            },
          ],
        },
      }),

      // 6. Replacement order creation (Shopify) — if needed
      shopifyNode(
        "6",
        "Create replacement order (Shopify)",
        1240,
        "POST",
        "orders.json",
        [
          {
            key: "order",
            value:
              '{"line_items":[{"variant_id":"{recall_sku}","quantity":1}],"email":"{recall_customer_email}","note":"Recall replacement - {recall_id}","tags":"recall,replacement,{recall_id}","financial_status":"paid"}',
          },
        ],
        [
          {
            name: "order.id",
            customName: "replacement_order_id",
            type: "string",
          },
          {
            name: "order.order_number",
            customName: "replacement_order_number",
            type: "string",
          },
        ],
      ),

      // 7. Response delivery
      n("7", "sendMessage", "Deliver recall response", 1480, {
        sendMessageDetails: {
          message:
            "Hi {recall_customer_name},\\n\\nThank you for contacting us about recall {recall_id} for the {recall_product_name}.\\n\\nReturn label: {return_label_url}\\nTracking: {return_tracking}\\nReplacement order: #{replacement_order_number}\\n\\nWe sincerely apologize for any inconvenience. Your safety is our top priority.\\n\\n[Track Return] [Track Replacement]",
        },
      }),

      // 8. Safety escalation (Slack)
      slackNode(
        "8",
        "Slack: Safety/recall escalation",
        1720,
        ":warning: Recall Support Alert\\nCustomer: {recall_customer_name} ({recall_customer_email})\\nProduct: {recall_product_name} (SKU: {recall_sku})\\nRecall: {recall_id}\\nIntent: {recall_intent} | Urgency: {recall_urgency}\\nSafety concern: {safety_concern}\\nReturn tracking: {return_tracking}\\nReplacement: #{replacement_order_number}",
      ),

      // 9. Recall audit logging (Sheets)
      sheetsNode(
        "9",
        "Log recall audit to Sheets",
        1960,
        "RecallAudit",
        '[["{recall_id}","{recall_customer_email}","{recall_customer_name}","{recall_sku}","{recall_product_name}","{recall_order_id}","{recall_intent}","{recall_urgency}","{safety_concern}","{return_tracking}","{replacement_order_number}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"), // needs label -> generate return label
      e("4", "7", "output_2"), // no label needed -> skip to response
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
      e("8", "9"),
    ],
  },

  // ===================================================================
  // 483. PRODUCT RECALL NOTIFICATION (8 nodes)
  // ===================================================================

  "Product Recall Notification": {
    summary:
      "Mass recall notification with severity-based routing and compliance tracking. Recall publish trigger identifies affected customers, severity routing sends recall emails to all and critical SMS to high-severity cases, tracks acknowledgments, builds unresponsive follow-up lists, and logs compliance audit data to Sheets.",
    nodes: [
      trigger("Webhook: Recall published", "webhook_app", {
        source: "Product Safety",
        event: "recall.published",
        howItStarts:
          "A product recall is officially published, triggering immediate notifications to all affected customers based on purchase history",
        setup:
          "Configure recall management system webhook. Set up customer database queries, Postmark for emails, Twilio for SMS, and Sheets for compliance.",
        inputFields: [],
      }),

      // 2. Affected customer identification (AI extraction)
      aiNode(
        "2",
        "AI extract recall details",
        280,
        "You are a recall data parser. Extract the recall ID, recalled product name, recalled SKUs, severity level (critical, moderate, low), recall reason, affected date range, recommended action, and company contact from the webhook payload regardless of format. Respond JSON: {recall_id: string, product_name: string, recalled_skus: string, severity: string, recall_reason: string, date_range: string, recommended_action: string, contact_info: string, affected_customer_count: number}",
        "{webhook_payload}",
        [
          { field: "recall_id", as: "notif_recall_id", type: "string" },
          { field: "product_name", as: "notif_product_name", type: "string" },
          { field: "recalled_skus", as: "recalled_skus", type: "string" },
          { field: "severity", as: "recall_severity", type: "string" },
          { field: "recall_reason", as: "recall_reason", type: "string" },
          {
            field: "recommended_action",
            as: "recommended_action",
            type: "string",
          },
          {
            field: "affected_customer_count",
            as: "affected_count",
            type: "number",
          },
          { field: "contact_info", as: "recall_contact", type: "string" },
        ],
      ),

      // 3. Severity condition
      n("3", "condition", "Critical severity recall?", 520, {
        conditionData: [
          {
            variable: "recall_severity",
            value: "critical",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 4. Recall email (Postmark) — all severities
      emailNode(
        "4",
        "Send recall notification email",
        760,
        "{affected_customers_email}",
        "IMPORTANT: Product Recall Notice - {notif_product_name} (Recall #{notif_recall_id})",
        "PRODUCT RECALL NOTICE\\n\\nRecall ID: {notif_recall_id}\\nProduct: {notif_product_name}\\nSKUs: {recalled_skus}\\nSeverity: {recall_severity}\\n\\nReason: {recall_reason}\\n\\nRecommended Action: {recommended_action}\\n\\nPlease take action immediately. Contact us at {recall_contact} for assistance.\\n\\nWe apologize for any inconvenience and appreciate your prompt attention to this matter.",
      ),

      // 5. Critical SMS (Twilio) — True path
      smsNode(
        "5",
        "Critical recall SMS alert",
        760,
        "{affected_customers_phone}",
        "URGENT RECALL: {notif_product_name} (#{notif_recall_id}). {recommended_action}. Contact: {recall_contact}. Details emailed.",
        400,
      ),

      // 6. Acknowledgment tracking
      n("6", "runCode", "Track acknowledgments", 1000, {
        runCodeDetails: {
          language: "javascript",
          code: `// Track recall notification acknowledgment status
const recallId = vars.notif_recall_id || "";
const affectedCount = parseInt(vars.affected_count) || 0;
const severity = vars.recall_severity || "unknown";
const sentAt = new Date().toISOString();
// In production: query email open/click tracking and SMS delivery status
const acknowledgment = {
  recall_id: recallId,
  notifications_sent: affectedCount,
  severity: severity,
  sent_at: sentAt,
  email_sent: "yes",
  sms_sent: severity === "critical" ? "yes" : "no",
  ack_rate: "pending",
  follow_up_needed: "pending_24h_check"
};
return acknowledgment;`,
          outputVariables: [
            { name: "notifications_sent", type: "number" },
            { name: "email_sent", type: "string" },
            { name: "sms_sent", type: "string" },
            { name: "sent_at", type: "string" },
            { name: "follow_up_needed", type: "string" },
          ],
        },
      }),

      // 7. Unresponsive follow-up list
      n("7", "runCode", "Build unresponsive follow-up list", 1240, {
        runCodeDetails: {
          language: "javascript",
          code: `// Build list of customers who haven't acknowledged the recall
const recallId = vars.notif_recall_id || "";
const totalSent = parseInt(vars.notifications_sent) || 0;
// In production: query CRM for non-openers after 24h
const result = {
  follow_up_list_status: "generated",
  total_notified: totalSent,
  estimated_unresponsive: Math.round(totalSent * 0.35),
  follow_up_channel: "phone_call",
  recall_reference: recallId
};
return result;`,
          outputVariables: [
            { name: "follow_up_list_status", type: "string" },
            { name: "total_notified", type: "number" },
            { name: "estimated_unresponsive", type: "number" },
            { name: "follow_up_channel", type: "string" },
          ],
        },
      }),

      // 8. Compliance audit (Sheets)
      sheetsNode(
        "8",
        "Log compliance audit to Sheets",
        1480,
        "RecallCompliance",
        '[["{notif_recall_id}","{notif_product_name}","{recalled_skus}","{recall_severity}","{recall_reason}","{affected_count}","{notifications_sent}","{email_sent}","{sms_sent}","{sent_at}","{estimated_unresponsive}","{follow_up_channel}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_1"), // critical -> email (then SMS)
      e("3", "4", "output_2"), // non-critical -> email only
      e("4", "5"), // SMS after email for critical
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 484. PRODUCT RECALL NOTIFICATION BOT (8 nodes)
  // ===================================================================

  "Product Recall Notification Bot": {
    summary:
      "Conversational recall notification bot for customer inquiries. Chat triggers active recall check, determines if the customer is affected, provides personalized next-steps, delivers via the conversation channel, creates Zendesk tickets for tracking, alerts compliance via Slack, and logs flow data for regulatory auditing.",
    nodes: [
      trigger("Chat trigger: Recall inquiry", "webhook_form", {
        source: "Chat Widget",
        event: "message.received",
        howItStarts:
          "A customer contacts the brand asking about product recalls, checking if their product is affected, or seeking recall instructions",
        setup:
          "Connect chat widget. Configure active recall database access. Set up Zendesk for ticket creation and Slack for compliance alerts.",
        inputFields: [],
      }),

      // 2. Active recall check (AI + data)
      aiNode(
        "2",
        "AI parse recall inquiry",
        280,
        "You are a recall inquiry parser. Extract the customer's question, their product details (name, SKU, serial number, purchase date), contact info, and the specific recall they are asking about. Check if they reference a known recall. Respond JSON: {customer_question: string, customer_email: string, customer_name: string, product_sku: string, product_name: string, serial_number: string, purchase_date: string, referenced_recall: string, inquiry_channel: string}",
        "{webhook_payload}",
        [
          {
            field: "customer_question",
            as: "bot_recall_question",
            type: "string",
          },
          { field: "customer_email", as: "bot_recall_email", type: "string" },
          { field: "customer_name", as: "bot_recall_name", type: "string" },
          { field: "product_sku", as: "bot_recall_sku", type: "string" },
          { field: "product_name", as: "bot_recall_product", type: "string" },
          { field: "serial_number", as: "bot_serial_number", type: "string" },
          {
            field: "referenced_recall",
            as: "bot_referenced_recall",
            type: "string",
          },
        ],
      ),

      // 3. Customer affected condition
      n("3", "hitApi", "Check active recalls database", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://{recall_api}/api/v1/recalls/check?sku={bot_recall_sku}&serial={bot_serial_number}",
          headers: [
            { key: "Authorization", value: "Bearer {recall_api_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "is_affected",
              customName: "bot_is_affected",
              type: "string",
            },
            {
              name: "recall_id",
              customName: "bot_active_recall_id",
              type: "string",
            },
            {
              name: "recall_details",
              customName: "bot_recall_details",
              type: "string",
            },
            {
              name: "recommended_action",
              customName: "bot_recommended_action",
              type: "string",
            },
          ],
        },
      }),

      // 4. Affected condition
      n("4", "condition", "Customer product is affected?", 760, {
        conditionData: [
          {
            variable: "bot_is_affected",
            value: "true",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 5. Personalized next-steps response (True — affected)
      aiNode(
        "5",
        "AI personalized next-steps",
        1000,
        "You are a compassionate recall support agent. The customer's product IS affected by an active recall. Provide clear, empathetic next steps: how to return the product, how to get a replacement or refund, safety precautions to take immediately. Be thorough but reassuring. Respond JSON: {response: string, safety_warning: string, return_instructions: string, timeline: string}",
        "Customer: {bot_recall_name}. Product: {bot_recall_product} (SKU: {bot_recall_sku}, Serial: {bot_serial_number}). Recall: {bot_active_recall_id}. Details: {bot_recall_details}. Recommended action: {bot_recommended_action}.",
        [
          { field: "response", as: "bot_recall_response", type: "string" },
          { field: "safety_warning", as: "bot_safety_warning", type: "string" },
          {
            field: "return_instructions",
            as: "bot_return_instructions",
            type: "string",
          },
        ],
      ),

      // 6. Channel delivery (both paths)
      n("6", "sendMessage", "Deliver recall response", 1240, {
        sendMessageDetails: {
          message:
            "{bot_recall_response}\\n\\nSafety Notice: {bot_safety_warning}\\n\\nReturn Instructions: {bot_return_instructions}\\n\\nIf you have additional questions, our team is here to help. [Start Return] [Talk to Agent]",
        },
      }),

      // 7. Recall-linked Zendesk ticket + Compliance Slack alert
      zendeskNode(
        "7",
        "Create recall Zendesk ticket",
        1480,
        "Recall Inquiry - {bot_recall_product} ({bot_active_recall_id})",
        "Customer: {bot_recall_name} ({bot_recall_email})\\nProduct: {bot_recall_product} (SKU: {bot_recall_sku})\\nSerial: {bot_serial_number}\\nAffected: {bot_is_affected}\\nRecall: {bot_active_recall_id}\\nAction taken: Automated recall response delivered",
        "high",
        [
          {
            name: "ticket.id",
            customName: "bot_recall_ticket_id",
            type: "string",
          },
        ],
      ),

      // 8. Compliance Slack alert + logging
      slackNode(
        "8",
        "Slack: Compliance alert + logging",
        1720,
        ":shield: Recall Bot Interaction\\nCustomer: {bot_recall_name} ({bot_recall_email})\\nProduct: {bot_recall_product} (SKU: {bot_recall_sku})\\nAffected: {bot_is_affected}\\nRecall: {bot_active_recall_id}\\nTicket: {bot_recall_ticket_id}\\nSafety warning issued: {bot_safety_warning}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"), // affected -> personalized response
      e("4", "6", "output_2"), // not affected -> generic response
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 485. PRODUCT RECOMMENDATION BOT (8 nodes)
  // ===================================================================

  "Product Recommendation Bot": {
    summary:
      "AI-powered recommendation chatbot with CDP personalization and conversational delivery. Chat triggers CDP context loading, AI classifies the request type, routing determines recommendation strategy, pulls from a recommender engine, generates conversational responses with product cards, and falls back to staff picks with ops alerts when needed.",
    nodes: [
      trigger("Chat trigger: Product recommendation request", "webhook_form", {
        source: "Chat Widget",
        event: "message.received",
        howItStarts:
          "A customer asks for product recommendations via chat, triggering personalized AI-powered suggestions based on their profile and browsing context",
        setup:
          "Connect chat widget. Configure CDP (Segment) for customer profiles. Set up recommender engine API and Shopify product catalog.",
        inputFields: [],
      }),

      // 2. CDP context load
      n("2", "hitApi", "Load customer context from CDP", 280, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.segment.io/v1/profiles/{customer_id}/traits",
          headers: [
            { key: "Authorization", value: "Basic {segment_write_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "traits.browse_history",
              customName: "rec_browse_history",
              type: "string",
            },
            {
              name: "traits.purchase_history",
              customName: "rec_purchase_history",
              type: "string",
            },
            {
              name: "traits.preferred_category",
              customName: "rec_preferred_category",
              type: "string",
            },
            {
              name: "traits.budget_range",
              customName: "rec_budget_range",
              type: "string",
            },
            {
              name: "traits.email",
              customName: "rec_customer_email",
              type: "string",
            },
          ],
        },
      }),

      // 3. AI request classification
      aiNode(
        "3",
        "AI request classification",
        520,
        "You are a shopping assistant request classifier. Given the customer's message and CDP profile, classify the recommendation request: 'specific' (looking for a particular type of product), 'gift' (buying for someone else), 'browse' (exploring options), or 'restock' (repurchasing consumables). Extract preferences and constraints. Respond JSON: {request_type: string, preferences: string, constraints: string, price_range: string, occasion: string, recipient: string, customer_id: string, session_id: string}",
        "Customer message: {webhook_payload}. Browse history: {rec_browse_history}. Purchases: {rec_purchase_history}. Preferred category: {rec_preferred_category}. Budget: {rec_budget_range}.",
        [
          { field: "request_type", as: "rec_request_type", type: "string" },
          { field: "preferences", as: "rec_preferences", type: "string" },
          { field: "constraints", as: "rec_constraints", type: "string" },
          { field: "price_range", as: "rec_price_range", type: "string" },
          { field: "occasion", as: "rec_occasion", type: "string" },
          { field: "customer_id", as: "rec_customer_id", type: "string" },
        ],
      ),

      // 4. Request type condition
      n("4", "condition", "Specific or gift request?", 760, {
        conditionData: [
          {
            variable: "rec_request_type",
            value: "specific",
            comparison_operator: "EQUAL TO",
            logical_operator: "OR",
          },
          {
            variable: "rec_request_type",
            value: "gift",
            comparison_operator: "EQUAL TO",
            logical_operator: "OR",
          },
        ],
      }),

      // 5. Recommender pull (True — specific/gift)
      n("5", "hitApi", "Pull from recommender engine", 1000, {
        hitApiDetails: {
          method: "POST",
          url: "https://{recommender_api}/api/v1/recommend",
          headers: [
            { key: "Authorization", value: "Bearer {recommender_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "customer_id", value: "{rec_customer_id}" },
            { key: "preferences", value: "{rec_preferences}" },
            { key: "constraints", value: "{rec_constraints}" },
            { key: "price_range", value: "{rec_price_range}" },
            { key: "request_type", value: "{rec_request_type}" },
          ],
          variables: [
            {
              name: "recommendations",
              customName: "rec_products",
              type: "string",
            },
            {
              name: "recommendations.length",
              customName: "rec_count",
              type: "number",
            },
            {
              name: "model_version",
              customName: "rec_model_version",
              type: "string",
            },
          ],
        },
      }),

      // 6. AI conversational response with product cards
      aiNode(
        "6",
        "AI conversational recommendation",
        1240,
        "You are a friendly shopping assistant. Given the recommendation results and customer context, craft a warm, conversational response presenting the products. Use engaging language, explain why each product fits their needs, and include price info. Format as a chat response with product cards. Respond JSON: {response: string, featured_product: string, products_shown: number, follow_up: string}",
        "Request: {rec_request_type}. Preferences: {rec_preferences}. Occasion: {rec_occasion}. Budget: {rec_price_range}. Recommendations: {rec_products}. Browse history: {rec_browse_history}.",
        [
          { field: "response", as: "rec_response", type: "string" },
          { field: "featured_product", as: "rec_featured", type: "string" },
          { field: "products_shown", as: "rec_products_shown", type: "number" },
          { field: "follow_up", as: "rec_follow_up", type: "string" },
        ],
      ),

      // 7. Channel delivery with cards
      n("7", "sendMessage", "Deliver recommendations with cards", 1480, {
        sendMessageDetails: {
          message:
            "{rec_response}\\n\\n{rec_follow_up}\\n\\n[View All Recommendations] [Refine Preferences] [Talk to Stylist]",
        },
      }),

      // 8. Staff picks fallback + ops alert (False — browse/restock)
      slackNode(
        "8",
        "Slack: Staff picks fallback + ops alert",
        1000,
        ":shopping_bags: Recommendation Bot Activity\\nCustomer: {rec_customer_email}\\nRequest type: {rec_request_type}\\nPreferences: {rec_preferences}\\nOccasion: {rec_occasion}\\nBudget: {rec_price_range}\\nProducts shown: {rec_products_shown}\\nFeatured: {rec_featured}\\nModel: {rec_model_version}",
        400,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"), // specific/gift -> recommender
      e("4", "8", "output_2"), // browse/restock -> staff picks fallback
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 486. PRODUCT TUTORIAL RECOMMENDATION BOT (8 nodes)
  // ===================================================================

  "Product Tutorial Recommendation Bot": {
    summary:
      "Recommends personalized tutorials after purchase or help requests. Post-purchase or help triggers product identification, CMS/video library search, content type routing, AI builds a learning path, delivers via email or chat, alerts Slack on content gaps, and logs tutorial engagement for analytics.",
    nodes: [
      trigger("Webhook: Post-purchase or help request", "webhook_app", {
        source: "Shopify / Help Center",
        event: "order.fulfilled OR help.requested",
        howItStarts:
          "A customer receives their order or requests help with a product, triggering personalized tutorial and learning content recommendations",
        setup:
          "Configure Shopify fulfillment webhook and help center triggers. Set up CMS API for tutorial content. Configure email and Slack.",
        inputFields: [],
      }),

      // 2. Product identification (AI)
      aiNode(
        "2",
        "AI identify product & context",
        280,
        "You are a product context parser. Extract the customer email, customer name, product ID, product name, product category, the trigger source (purchase or help), and any specific help topic from the payload regardless of format. Respond JSON: {customer_email: string, customer_name: string, product_id: string, product_name: string, product_category: string, trigger_source: string, help_topic: string, skill_level: string}",
        "{webhook_payload}",
        [
          { field: "customer_email", as: "tut_customer_email", type: "string" },
          { field: "customer_name", as: "tut_customer_name", type: "string" },
          { field: "product_name", as: "tut_product_name", type: "string" },
          {
            field: "product_category",
            as: "tut_product_category",
            type: "string",
          },
          { field: "trigger_source", as: "tut_trigger_source", type: "string" },
          { field: "help_topic", as: "tut_help_topic", type: "string" },
          { field: "skill_level", as: "tut_skill_level", type: "string" },
        ],
      ),

      // 3. CMS/video library search
      n("3", "hitApi", "Search CMS & video library", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://{cms_api}/api/v1/content?product={tut_product_name}&category={tut_product_category}&topic={tut_help_topic}&type=tutorial,video,guide",
          headers: [
            { key: "Authorization", value: "Bearer {cms_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "results",
              customName: "tut_content_results",
              type: "string",
            },
            { name: "total", customName: "tut_content_count", type: "number" },
            {
              name: "content_types",
              customName: "tut_content_types",
              type: "string",
            },
          ],
        },
      }),

      // 4. Content type condition
      n("4", "condition", "Tutorial content found?", 760, {
        conditionData: [
          {
            variable: "tut_content_count",
            value: "0",
            comparison_operator: "GREATER THAN",
            logical_operator: "AND",
          },
        ],
      }),

      // 5. AI learning path (True)
      aiNode(
        "5",
        "AI build learning path",
        1000,
        "You are a product education specialist. Given the available tutorials and the customer's context (product, skill level, help topic), create a personalized learning path. Order content from beginner to advanced, prioritize video content, and include estimated completion times. Respond JSON: {learning_path: string, estimated_time: string, first_tutorial: string, email_subject: string, email_body: string, path_length: number}",
        "Customer: {tut_customer_name}. Product: {tut_product_name} ({tut_product_category}). Trigger: {tut_trigger_source}. Help topic: {tut_help_topic}. Skill level: {tut_skill_level}. Available content ({tut_content_count} items): {tut_content_results}.",
        [
          { field: "learning_path", as: "tut_learning_path", type: "string" },
          { field: "estimated_time", as: "tut_estimated_time", type: "string" },
          { field: "email_subject", as: "tut_email_subject", type: "string" },
          { field: "email_body", as: "tut_email_body", type: "string" },
          { field: "path_length", as: "tut_path_length", type: "number" },
        ],
      ),

      // 6. Email/chat delivery
      emailNode(
        "6",
        "Send tutorial recommendation email",
        1240,
        "{tut_customer_email}",
        "{tut_email_subject}",
        "Hi {tut_customer_name},\\n\\n{tut_email_body}\\n\\nEstimated learning time: {tut_estimated_time}\\n\\nStart your learning journey today!\\n\\n[Start First Tutorial] [View All Tutorials] [Get Help]",
      ),

      // 7. Content gap alert (Slack) — False path
      slackNode(
        "7",
        "Slack: Tutorial content gap alert",
        1000,
        ":books: Tutorial Content Gap\\nProduct: {tut_product_name} ({tut_product_category})\\nHelp topic: {tut_help_topic}\\nCustomer: {tut_customer_name} ({tut_customer_email})\\nTrigger: {tut_trigger_source}\\nSkill level: {tut_skill_level}\\nNo tutorial content found. Please create content for this product/topic.",
        400,
      ),

      // 8. Tutorial engagement logging (Sheets)
      sheetsNode(
        "8",
        "Log tutorial engagement to Sheets",
        1480,
        "TutorialEngagement",
        '[["{tut_customer_email}","{tut_customer_name}","{tut_product_name}","{tut_product_category}","{tut_trigger_source}","{tut_help_topic}","{tut_skill_level}","{tut_content_count}","{tut_path_length}","{tut_estimated_time}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"), // content found -> learning path
      e("4", "7", "output_2"), // no content -> content gap alert
      e("5", "6"),
      e("6", "8"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 487. PRODUCT VARIANT SYNC PIPELINE (9 nodes)
  // ===================================================================

  "Product Variant Sync Pipeline": {
    summary:
      "Syncs product variants across Amazon, Google Merchant, and eBay. Variant create/update triggers attribute validation via runCode, builds parent-child variant models, routes to platform-specific sync endpoints (Amazon SP-API, Google Merchant Center, eBay), aggregates results, and sends a Slack summary with Sheets audit logging.",
    nodes: [
      trigger("Webhook: Variant create/update", "webhook_app", {
        source: "Shopify",
        event: "products/update",
        howItStarts:
          "A product variant is created or updated in Shopify, triggering synchronization of variant data to Amazon, Google Merchant Center, and eBay",
        setup:
          "Configure Shopify webhook for products/update. Set up Amazon SP-API, Google Merchant Center, and eBay Trading API credentials.",
        inputFields: [],
      }),

      // 2. Attribute validation (runCode)
      n("2", "runCode", "Validate variant attributes", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Validate variant attributes for multi-platform sync
const payload = JSON.parse(vars.webhook_payload || "{}");
const product = payload.product || payload;
const variants = product.variants || [];
const title = product.title || "";
const productId = String(product.id || "");
const validated = variants.map((v, i) => {
  const issues = [];
  if (!v.sku) issues.push("missing_sku");
  if (!v.price || parseFloat(v.price) <= 0) issues.push("invalid_price");
  if (!v.barcode && !v.gtin) issues.push("missing_gtin");
  if (v.inventory_quantity === undefined) issues.push("missing_inventory");
  return {
    variant_id: String(v.id || i),
    sku: v.sku || "",
    price: v.price || "0",
    gtin: v.barcode || v.gtin || "",
    inventory: v.inventory_quantity || 0,
    option1: v.option1 || "",
    option2: v.option2 || "",
    option3: v.option3 || "",
    weight: v.weight || 0,
    weight_unit: v.weight_unit || "kg",
    issues: issues,
    valid: issues.length === 0
  };
});
const validCount = validated.filter(v => v.valid).length;
const invalidCount = validated.filter(v => !v.valid).length;
return {
  product_id: productId,
  product_title: title,
  variants_json: JSON.stringify(validated),
  variant_count: variants.length,
  valid_variants: validCount,
  invalid_variants: invalidCount,
  sync_ready: invalidCount === 0 ? "yes" : "partial"
};`,
          outputVariables: [
            { name: "product_id", type: "string" },
            { name: "product_title", type: "string" },
            { name: "variants_json", type: "string" },
            { name: "variant_count", type: "number" },
            { name: "valid_variants", type: "number" },
            { name: "invalid_variants", type: "number" },
            { name: "sync_ready", type: "string" },
          ],
        },
      }),

      // 3. Parent-child model build (runCode)
      n("3", "runCode", "Build parent-child variant model", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Build platform-specific parent-child variant structures
const variants = JSON.parse(vars.variants_json || "[]");
const title = vars.product_title || "";
const productId = vars.product_id || "";
// Amazon variation theme detection
const options = variants.map(v => [v.option1, v.option2, v.option3].filter(Boolean));
const variationTheme = options[0]?.length >= 2 ? "SizeColor" : options[0]?.length === 1 ? "Size" : "SingleVariant";
// Google group ID
const googleGroupId = "shopify_" + productId;
// eBay variation specifics
const ebaySpecifics = [...new Set(variants.flatMap(v => [v.option1, v.option2].filter(Boolean)))];
return {
  amazon_variation_theme: variationTheme,
  amazon_parent_sku: "PARENT_" + productId,
  google_group_id: googleGroupId,
  ebay_variation_specifics: JSON.stringify(ebaySpecifics),
  platform_models_built: "amazon,google,ebay",
  model_build_status: "complete"
};`,
          outputVariables: [
            { name: "amazon_variation_theme", type: "string" },
            { name: "amazon_parent_sku", type: "string" },
            { name: "google_group_id", type: "string" },
            { name: "ebay_variation_specifics", type: "string" },
            { name: "platform_models_built", type: "string" },
            { name: "model_build_status", type: "string" },
          ],
        },
      }),

      // 4. Amazon variation sync (SP-API)
      n("4", "hitApi", "Amazon SP-API variation sync", 760, {
        hitApiDetails: {
          method: "POST",
          url: "https://sellingpartnerapi-na.amazon.com/listings/2021-08-01/items/{amazon_seller_id}/{amazon_parent_sku}",
          headers: [
            { key: "x-amz-access-token", value: "{amazon_access_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "productType", value: "PRODUCT" },
            { key: "requirements", value: "LISTING" },
            {
              key: "attributes",
              value:
                '{"variation_theme":[{"value":"{amazon_variation_theme}"}],"child_skus":"{variants_json}"}',
            },
          ],
          variables: [
            {
              name: "status",
              customName: "amazon_sync_status",
              type: "string",
            },
            {
              name: "submissionId",
              customName: "amazon_submission_id",
              type: "string",
            },
            { name: "issues", customName: "amazon_issues", type: "string" },
          ],
        },
      }),

      // 5. Google group ID sync (Merchant Center)
      n("5", "hitApi", "Google Merchant variant sync", 1000, {
        hitApiDetails: {
          method: "POST",
          url: "https://shoppingcontent.googleapis.com/content/v2.1/{merchant_id}/products/batch",
          headers: [
            { key: "Authorization", value: "Bearer {google_merchant_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "entries",
              value:
                '[{"method":"insert","product":{"offerId":"{product_id}","itemGroupId":"{google_group_id}","title":"{product_title}","variants":"{variants_json}"}}]',
            },
          ],
          variables: [
            {
              name: "entries[0].kind",
              customName: "google_sync_status",
              type: "string",
            },
            {
              name: "entries[0].errors",
              customName: "google_errors",
              type: "string",
            },
          ],
        },
      }),

      // 6. eBay matrix sync
      n("6", "hitApi", "eBay Trading API variation sync", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.ebay.com/ws/api.dll",
          headers: [
            { key: "X-EBAY-API-CALL-NAME", value: "ReviseFixedPriceItem" },
            { key: "X-EBAY-API-SITEID", value: "0" },
            { key: "X-EBAY-API-COMPATIBILITY-LEVEL", value: "1225" },
            { key: "X-EBAY-API-IAF-TOKEN", value: "{ebay_oauth_token}" },
            { key: "Content-Type", value: "text/xml" },
          ],
          body: [
            {
              key: "xml_body",
              value:
                '<ReviseFixedPriceItemRequest xmlns="urn:ebay:apis:eBLBaseComponents"><Item><SKU>{amazon_parent_sku}</SKU><Variations>{ebay_variation_specifics}</Variations></Item></ReviseFixedPriceItemRequest>',
            },
          ],
          variables: [
            { name: "Ack", customName: "ebay_sync_status", type: "string" },
            { name: "Errors", customName: "ebay_errors", type: "string" },
          ],
        },
      }),

      // 7. Results aggregation (runCode)
      n("7", "runCode", "Aggregate sync results", 1480, {
        runCodeDetails: {
          language: "javascript",
          code: `// Aggregate multi-platform sync results
const amazon = vars.amazon_sync_status || "unknown";
const google = vars.google_sync_status || "unknown";
const ebay = vars.ebay_sync_status || "unknown";
const amazonOk = amazon === "ACCEPTED" || amazon === "VALID";
const googleOk = !vars.google_errors || vars.google_errors === "null";
const ebayOk = ebay === "Success" || ebay === "Warning";
const successCount = [amazonOk, googleOk, ebayOk].filter(Boolean).length;
const failCount = 3 - successCount;
const overallStatus = failCount === 0 ? "all_synced" : failCount === 3 ? "all_failed" : "partial";
return {
  sync_summary: "Amazon: " + amazon + " | Google: " + (googleOk ? "OK" : "Error") + " | eBay: " + ebay,
  platforms_synced: successCount,
  platforms_failed: failCount,
  overall_sync_status: overallStatus,
  amazon_result: amazon,
  google_result: googleOk ? "synced" : "error",
  ebay_result: ebay
};`,
          outputVariables: [
            { name: "sync_summary", type: "string" },
            { name: "platforms_synced", type: "number" },
            { name: "platforms_failed", type: "number" },
            { name: "overall_sync_status", type: "string" },
            { name: "amazon_result", type: "string" },
            { name: "google_result", type: "string" },
            { name: "ebay_result", type: "string" },
          ],
        },
      }),

      // 8. Slack summary
      slackNode(
        "8",
        "Slack: Variant sync summary",
        1720,
        ":arrows_counterclockwise: Variant Sync Complete\\nProduct: {product_title} (ID: {product_id})\\nVariants: {variant_count} ({valid_variants} valid, {invalid_variants} invalid)\\nSync: {overall_sync_status}\\n{sync_summary}\\nPlatforms synced: {platforms_synced}/3\\nAmazon submission: {amazon_submission_id}\\nGoogle group: {google_group_id}",
      ),

      // 9. Audit (Sheets)
      sheetsNode(
        "9",
        "Log variant sync audit to Sheets",
        1960,
        "VariantSyncAudit",
        '[["{product_id}","{product_title}","{variant_count}","{valid_variants}","{invalid_variants}","{amazon_variation_theme}","{amazon_result}","{google_result}","{ebay_result}","{overall_sync_status}","{platforms_synced}","{platforms_failed}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
      e("8", "9"),
    ],
  },
};
